<template>
  <div class="aigc-video-page">
    <div class="page-head">
      <div>
        <h2>AI视频生成</h2>
        <p>提交视频生成任务，并查看任务状态、成片地址和事件日志</p>
      </div>
      <div class="head-actions">
        <el-button size="mini" icon="el-icon-share" :loading="shareLoading" @click="openShareDialog">分享设置</el-button>
        <el-button size="mini" icon="el-icon-refresh" :loading="taskLoading || billingLoading" @click="refreshData">刷新</el-button>
      </div>
    </div>

    <el-tabs v-model="activeMainTab" class="aigc-main-tabs">
      <el-tab-pane label="创作页" name="creative">
        <section class="creative-workspace">
          <div class="creative-main">
            <div class="creative-greeting">
              <span class="creative-kicker">AI 视频创作</span>
              <h3>你好，想创作什么？</h3>
              <p>写下画面、主体、动作和镜头语言，选择模式后直接提交生成。</p>
            </div>

            <div class="creative-preset-bar">
              <div class="creative-preset-label">
                <i class="el-icon-collection-tag"></i>
                <span>参数预设</span>
              </div>
              <div class="creative-preset-actions">
                <el-select
                    v-model="selectedCreativePresetId"
                    size="mini"
                    clearable
                    placeholder="选择常用配置"
                    @change="applyCreativePreset">
                  <el-option
                      v-for="preset in creativePresets"
                      :key="preset.id"
                      :label="preset.name"
                      :value="preset.id">
                  </el-option>
                </el-select>
                <el-button size="mini" icon="el-icon-plus" @click="openCreativePresetDialog">保存当前</el-button>
                <el-button
                    size="mini"
                    icon="el-icon-delete"
                    :disabled="!selectedCreativePresetId"
                    @click="deleteCreativePreset">
                </el-button>
              </div>
            </div>

            <el-form
                ref="creativeCreateForm"
                :model="createForm"
                :rules="createRules"
                label-position="top"
                size="mini"
                class="creative-form">
              <el-form-item prop="prompt" class="creative-prompt-form-item">
                <el-input
                    ref="creativePromptEditor"
                    v-model="createForm.prompt"
                    class="creative-prompt-editor"
                    type="textarea"
                    :rows="8"
                    maxlength="5000"
                    show-word-limit
                    placeholder="描述你想生成的视频：主体、场景、动作、镜头、光线、风格、节奏..."
                    @input="handlePromptInput"
                    @blur="scheduleClosePromptMentionMenu"
                    @keydown.native="handlePromptKeydown">
                </el-input>
                <div class="creative-prompt-toolbar">
                  <el-button type="text" size="mini" class="prompt-mention-trigger" @mousedown.native.prevent="openPromptMentionMenu">@ 引用素材</el-button>
                  <el-button type="text" size="mini" icon="el-icon-time" @click="openPromptHistoryDialog">历史提示词</el-button>
                  <span v-if="continuedFromTask" class="continue-hint">已引用任务 #{{ continuedFromTask.id }}</span>
                </div>
                <transition name="el-zoom-in-top">
                  <div v-show="promptMentionMenuOpen" class="prompt-mention-menu" @mousedown.stop>
                    <div class="prompt-mention-head">
                      <span>引用素材</span>
                      <small>输入 @ 快速选择</small>
                    </div>
                    <div class="prompt-mention-list">
                      <button
                          v-for="(entry, index) in promptMentionEntries"
                          :key="entry.key"
                          type="button"
                          class="prompt-mention-item"
                          :class="{ active: promptMentionActiveIndex === index }"
                          @mouseenter="promptMentionActiveIndex = index"
                          @mousedown.prevent="selectPromptMentionEntry(entry)">
                        <template v-if="entry.kind === 'media'">
                          <img v-if="entry.item.type === 'image' && entry.item.previewUrl" :src="entry.item.previewUrl" :alt="entry.item.name">
                          <video v-else-if="entry.item.type === 'video' && entry.item.previewUrl" :src="entry.item.previewUrl" muted preload="metadata"></video>
                          <span v-else class="prompt-mention-icon"><i :class="mediaMentionIcon(entry.item.type)"></i></span>
                          <span class="prompt-mention-copy">
                            <strong>{{ entry.item.name }}</strong>
                            <small>{{ roleLabel(entry.item.role) || mediaTypeLabel(entry.item.type) }}</small>
                          </span>
                        </template>
                        <template v-else-if="entry.kind === 'upload'">
                          <span class="prompt-mention-icon add"><i class="el-icon-upload2"></i></span>
                          <span class="prompt-mention-copy">
                            <strong>从本地上传素材</strong>
                            <small>支持多选图片、视频和音频</small>
                          </span>
                          <i class="el-icon-arrow-right prompt-mention-arrow"></i>
                        </template>
                        <template v-else>
                          <span class="prompt-mention-icon add"><i :class="mediaMentionIcon(entry.mediaType)"></i></span>
                          <span class="prompt-mention-copy">
                            <strong>从图库添加{{ mediaTypeLabel(entry.mediaType) }}</strong>
                            <small>选择后插入提示词</small>
                          </span>
                          <i class="el-icon-arrow-right prompt-mention-arrow"></i>
                        </template>
                      </button>
                    </div>
                  </div>
                </transition>
                <input
                    ref="promptMentionUploadInput"
                    class="prompt-mention-file-input"
                    type="file"
                    multiple
                    :accept="mediaAccept()"
                    @change="handlePromptMentionLocalFiles">
              </el-form-item>

              <div class="creative-mode-grid">
                <button
                    v-for="scene in sceneOptions"
                    :key="scene.value"
                    type="button"
                    class="creative-mode-card"
                    :class="{ active: createForm.sceneType === scene.value }"
                    @click="setCreativeScene(scene.value)">
                  <i :class="sceneIcon(scene.value)"></i>
                  <span>{{ scene.label }}</span>
                  <small>{{ scene.hint }}</small>
                </button>
              </div>

              <div class="creative-settings">
                <el-form-item label="服务商" prop="provider">
                  <el-select v-model="createForm.provider" style="width: 100%" :disabled="!videoProviderOptions.length" @change="handleProviderChange">
                    <el-option
                        v-for="provider in videoProviderOptions"
                        :key="provider.value"
                        :label="provider.label"
                        :value="provider.value">
                    </el-option>
                  </el-select>
                </el-form-item>
                <el-form-item v-if="showProviderKeySelect" label="Key" prop="providerKeyId">
                  <el-select
                      v-model="createForm.providerKeyId"
                      style="width: 100%"
                      placeholder="选择 Seedance Key"
                      :disabled="!currentProviderKeyOptions.length"
                      @change="handleProviderKeyChange">
                    <el-option
                        v-for="key in currentProviderKeyOptions"
                        :key="key.optionKey"
                        :label="key.label"
                        :value="key.value">
                      <div class="provider-key-option">
                        <span>{{ key.label }}</span>
                        <small v-if="key.apiKeyPreview">{{ key.apiKeyPreview }}</small>
                      </div>
                    </el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="模型" prop="model">
                  <el-select
                      v-model="createForm.model"
                      filterable
                      style="width: 100%"
                      placeholder="选择模型"
                      @change="handleModelChange">
                    <el-option
                        v-for="model in currentModelOptions"
                        :key="model.value"
                        :label="model.label"
                        :value="model.value">
                    </el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="清晰度">
                  <el-select v-model="createForm.resolution" placeholder="选择清晰度">
                    <el-option v-for="option in currentResolutionOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="比例">
                  <el-select v-model="createForm.ratio" placeholder="选择比例">
                    <el-option v-for="option in currentRatioOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="时长">
                  <el-input-number v-model="createForm.duration" :min="currentDurationRange.min" :max="currentDurationRange.max" controls-position="right" style="width: 100%"></el-input-number>
                </el-form-item>
                <el-form-item label="声音">
                  <div class="creative-audio-control">
                    <el-switch v-model="createForm.generateAudio" disabled></el-switch>
                    <span>{{ createForm.generateAudio ? '生成' : '关闭' }}</span>
                  </div>
                </el-form-item>
              </div>

              <div class="creative-advanced-toggle">
                <el-button type="text" size="mini" @click="creativeAdvancedOpen = !creativeAdvancedOpen">
                  高级参数<i :class="creativeAdvancedOpen ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                </el-button>
              </div>
              <el-collapse-transition>
                <div v-show="creativeAdvancedOpen" class="creative-advanced-settings">
                  <el-form-item label="随机种子">
                    <el-input-number v-model="createForm.seed" :min="0" controls-position="right" style="width: 100%"></el-input-number>
                  </el-form-item>
                </div>
              </el-collapse-transition>

              <div class="creative-material-panel">
                <div class="creative-section-head">
                  <div>
                    <strong>参考素材</strong>
                    <span>{{ mediaPreviewItems.length }} 个</span>
                  </div>
                  <div class="creative-material-actions">
                    <el-upload
                        action="#"
                        :show-file-list="false"
                        :accept="mediaAccept('image')"
                        :before-upload="beforeMediaUpload"
                        :http-request="request => uploadCreativeMedia(request, 'image')">
                      <el-button size="mini" icon="el-icon-picture-outline">上传图片</el-button>
                    </el-upload>
                    <el-upload
                        action="#"
                        :show-file-list="false"
                        :accept="mediaAccept('video')"
                        :before-upload="beforeMediaUpload"
                        :http-request="request => uploadCreativeMedia(request, 'video')">
                      <el-button size="mini" icon="el-icon-video-camera">上传视频</el-button>
                    </el-upload>
                    <el-upload
                        action="#"
                        :show-file-list="false"
                        :accept="mediaAccept('audio')"
                        :before-upload="beforeMediaUpload"
                        :http-request="request => uploadCreativeMedia(request, 'audio')">
                      <el-button size="mini" icon="el-icon-headset">上传音频</el-button>
                    </el-upload>
                    <el-dropdown trigger="click" @command="openCreativeMediaPicker">
                      <el-button size="mini" icon="el-icon-folder-opened">从图库选择<i class="el-icon-arrow-down el-icon--right"></i></el-button>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="image" icon="el-icon-picture-outline">图片</el-dropdown-item>
                        <el-dropdown-item command="video" icon="el-icon-video-camera">视频</el-dropdown-item>
                        <el-dropdown-item command="audio" icon="el-icon-headset">音频</el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </div>
                </div>

                <div v-if="mediaPreviewItems.length" class="creative-media-grid">
                  <div v-for="item in mediaPreviewItems" :key="item.key" class="creative-media-card">
                    <img v-if="item.type === 'image' && item.previewUrl" :src="item.previewUrl" :alt="item.name">
                    <video v-else-if="item.type === 'video' && item.previewUrl" :src="item.previewUrl" controls preload="metadata" playsinline @click.stop></video>
                    <div v-else-if="item.type === 'audio' && item.previewUrl" class="creative-audio-preview">
                      <i class="el-icon-headset"></i>
                      <audio :src="item.previewUrl" controls preload="metadata" @click.stop></audio>
                    </div>
                    <div v-else class="creative-media-fallback">{{ mediaTypeLabel(item.type) }}</div>
                    <div class="creative-media-meta">
                      <span>{{ roleLabel(item.role) || mediaTypeLabel(item.type) }}</span>
                      <div class="creative-media-card-actions">
                        <el-button type="text" size="mini" title="插入提示词引用" @click="insertMediaMentionFromCard(item.index)">@</el-button>
                        <el-button type="text" size="mini" icon="el-icon-delete" class="danger-link" title="删除素材" @click="removeMedia(item.index)"></el-button>
                      </div>
                    </div>
                    <div class="creative-media-name" :title="item.name">{{ item.name }}</div>
                  </div>
                </div>
                <div v-else class="creative-empty-material">
                  <i class="el-icon-picture-outline"></i>
                  <span>可上传参考图、参考视频，或从素材库选择</span>
                </div>

                <div v-for="(item, index) in createForm.media" :key="'creative-media-' + index" class="creative-media-line">
                  <el-select v-model="item.type" size="mini" class="media-type" @change="handleMediaTypeChange(item)">
                    <el-option label="图片" value="image"></el-option>
                    <el-option label="视频" value="video"></el-option>
                    <el-option label="音频" value="audio"></el-option>
                  </el-select>
                  <el-select v-model="item.role" size="mini" clearable placeholder="角色" class="media-role">
                    <el-option label="首帧" value="first_frame"></el-option>
                    <el-option label="尾帧" value="last_frame"></el-option>
                    <el-option label="参考图" value="reference_image"></el-option>
                    <el-option label="参考视频" value="reference_video"></el-option>
                    <el-option label="参考音频" value="reference_audio"></el-option>
                    <el-option label="视频" value="video"></el-option>
                  </el-select>
                  <el-input v-model.trim="item.url" size="mini" placeholder="粘贴素材 URL"></el-input>
                  <el-button size="mini" icon="el-icon-folder-opened" @click="openMediaPicker(item)">选择</el-button>
                  <el-button type="text" size="mini" icon="el-icon-delete" class="danger-link" @click="removeMedia(index)"></el-button>
                </div>
                <el-button type="text" size="mini" icon="el-icon-plus" @click="addMedia">添加素材 URL</el-button>
              </div>
            </el-form>
          </div>

          <aside class="creative-side">
            <div class="creative-submit-card">
              <div>
                <strong>{{ sceneLabel(createForm.sceneType) }}</strong>
                <span>{{ modelDisplayName(createForm.model, createForm.provider) }}</span>
              </div>
              <el-button type="primary" icon="el-icon-video-camera" :loading="creating" :disabled="!videoProviderOptions.length" @click="createTask">生成视频</el-button>
              <div class="creative-cost-estimate" v-loading="billingEstimateLoading">
                <div class="creative-cost-head">
                  <span>预估费用</span>
                  <strong v-if="billingEstimate.available">{{ formatCurrency(billingEstimate.estimatedCost) }}</strong>
                  <strong v-else>-</strong>
                </div>
                <template v-if="billingEstimate.available">
                  <div class="creative-cost-detail">
                    <span>约 {{ formatInteger(billingEstimate.estimatedUsageTokens) }} Tokens</span>
                    <span>{{ billingUnitPriceText(billingEstimate) }}</span>
                  </div>
                  <small :title="billingEstimate.pricingNote">{{ billingEstimate.pricingRule || billingEstimate.pricingNote }}</small>
                </template>
                <small v-else>{{ billingEstimate.pricingNote || '完善模型参数后显示' }}</small>
              </div>
              <div class="creative-submit-meta">
                <span>{{ createForm.resolution || '默认清晰度' }}</span>
                <span>{{ createForm.ratio || '默认比例' }}</span>
                <span>{{ createForm.duration }} 秒</span>
                <span v-if="showProviderKeySelect">{{ selectedProviderKeyLabel }}</span>
                <span>{{ createForm.generateAudio ? '生成声音' : '无声音' }}</span>
              </div>
            </div>

            <div class="creative-recent-card">
              <div class="creative-section-head compact">
                <strong>最近任务</strong>
                <el-button type="text" size="mini" @click="activeMainTab = 'manage'">查看全部</el-button>
              </div>
              <div v-if="recentCreativeResults.length" class="creative-recent-list">
                <div v-for="row in recentCreativeResults" :key="row.id" class="creative-recent-item">
                  <video
                      v-if="row.resultUrl || row.providerResultUrl"
                      :src="publicUrl(row.resultUrl || row.providerResultUrl)"
                      :poster="publicUrl(row.coverUrl)"
                      preload="metadata"
                      muted
                      playsinline>
                  </video>
                  <div v-else class="creative-media-fallback creative-task-fallback">
                    <i :class="sceneIcon(row.sceneType)"></i>
                  </div>
                  <div class="creative-recent-info">
                    <div class="creative-recent-title">
                      <strong>{{ sceneLabel(row.sceneType) }}</strong>
                      <el-tag size="mini" :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
                    </div>
                    <span>{{ formatTime(row.recordCreatedAt) }}</span>
                    <div class="creative-recent-actions">
                      <el-button type="text" size="mini" :disabled="!creativeResultUrl(row)" @click="openVideoPreview(row, creativeResultUrl(row))">预览</el-button>
                      <el-button type="text" size="mini" :disabled="!creativeResultUrl(row)" @click="downloadCreativeResult(row)">下载</el-button>
                      <el-button type="text" size="mini" :disabled="!row.prompt" @click="continueWithPrompt(row)">继续</el-button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="creative-empty-recent">暂无任务</div>
            </div>
          </aside>
        </section>
      </el-tab-pane>

      <el-tab-pane label="任务管理" name="manage">
    <section class="panel create-panel">
      <div class="panel-title">
        <span>创建生成任务</span>
        <el-button type="primary" size="mini" icon="el-icon-video-camera" :loading="creating" :disabled="!videoProviderOptions.length" @click="createTask">提交任务</el-button>
      </div>
      <div class="create-body">
        <el-form ref="createForm" :model="createForm" :rules="createRules" label-width="96px" size="mini" class="create-form">
          <el-row :gutter="16">
            <el-col :span="showProviderKeySelect ? 6 : 8">
              <el-form-item label="服务商" prop="provider">
                <el-select v-model="createForm.provider" style="width: 100%" :disabled="!videoProviderOptions.length" @change="handleProviderChange">
                  <el-option
                      v-for="provider in videoProviderOptions"
                      :key="provider.value"
                      :label="provider.label"
                      :value="provider.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="showProviderKeySelect ? 6 : 8">
              <el-form-item label="生成场景" prop="sceneType">
                <el-select v-model="createForm.sceneType" style="width: 100%" @change="handleSceneChange">
                  <el-option v-for="scene in sceneOptions" :key="scene.value" :label="scene.label" :value="scene.value"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="showProviderKeySelect ? 6 : 8">
              <el-form-item label="模型" prop="model">
                <el-select
                    v-model="createForm.model"
                    filterable
                    style="width: 100%"
                    placeholder="选择模型"
                    @change="handleModelChange">
                  <el-option
                      v-for="model in currentModelOptions"
                      :key="model.value"
                      :label="model.label"
                      :value="model.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col v-if="showProviderKeySelect" :span="6">
              <el-form-item label="Key" prop="providerKeyId">
                <el-select
                    v-model="createForm.providerKeyId"
                    style="width: 100%"
                    placeholder="选择 Seedance Key"
                    :disabled="!currentProviderKeyOptions.length"
                    @change="handleProviderKeyChange">
                  <el-option
                      v-for="key in currentProviderKeyOptions"
                      :key="key.optionKey"
                      :label="key.label"
                      :value="key.value">
                    <div class="provider-key-option">
                      <span>{{ key.label }}</span>
                      <small v-if="key.apiKeyPreview">{{ key.apiKeyPreview }}</small>
                    </div>
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="提示词" prop="prompt">
            <el-input
                v-model="createForm.prompt"
                type="textarea"
                :rows="4"
                maxlength="5000"
                show-word-limit
                placeholder="描述要生成的视频内容、主体、动作、镜头、风格等"
                @keydown.native="handlePromptKeydown">
            </el-input>
            <div class="prompt-actions">
              <el-button type="text" size="mini" icon="el-icon-time" @click="openPromptHistoryDialog">使用之前的提示词</el-button>
              <span v-if="continuedFromTask" class="continue-hint">已引用任务 #{{ continuedFromTask.id }}</span>
            </div>
          </el-form-item>

          <div class="media-block">
            <div class="sub-title">
              <span>媒体素材 URL</span>
              <el-button type="text" size="mini" icon="el-icon-plus" @click="addMedia">添加素材</el-button>
            </div>
            <div v-for="(item, index) in createForm.media" :key="index" class="media-row">
              <el-select v-model="item.type" size="mini" class="media-type" @change="handleMediaTypeChange(item)">
                <el-option label="图片" value="image"></el-option>
                <el-option label="视频" value="video"></el-option>
                <el-option label="音频" value="audio"></el-option>
              </el-select>
              <el-select v-model="item.role" size="mini" clearable placeholder="角色" class="media-role">
                <el-option label="首帧" value="first_frame"></el-option>
                <el-option label="尾帧" value="last_frame"></el-option>
                <el-option label="参考图" value="reference_image"></el-option>
                <el-option label="参考视频" value="reference_video"></el-option>
                <el-option label="参考音频" value="reference_audio"></el-option>
                <el-option label="视频" value="video"></el-option>
              </el-select>
              <el-input v-model.trim="item.url" size="mini" placeholder="https:// 或 cos:// 素材地址"></el-input>
              <el-upload
                  action="#"
                  :show-file-list="false"
                  multiple
                  :accept="mediaAccept()"
                  :before-upload="file => beforeMediaUpload(file, item)"
                  :http-request="request => uploadMedia(request, item)">
                <el-button size="mini" icon="el-icon-upload2" :loading="item.uploading">上传</el-button>
              </el-upload>
              <el-button size="mini" icon="el-icon-folder-opened" @click="openMediaPicker(item)">选择</el-button>
              <el-button type="text" size="mini" icon="el-icon-delete" class="danger-link" @click="removeMedia(index)"></el-button>
              <span class="uploaded-name" v-if="item.fileName">{{ item.fileName }}</span>
            </div>
          </div>

          <el-row :gutter="24">
            <el-col :span="6">
              <el-form-item label="清晰度">
                <el-select v-model="createForm.resolution" placeholder="选择清晰度">
                  <el-option
                      v-for="option in currentResolutionOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="比例">
                <el-select v-model="createForm.ratio" placeholder="选择比例">
                  <el-option
                      v-for="option in currentRatioOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="时长">
                <el-input-number v-model="createForm.duration" :min="currentDurationRange.min" :max="currentDurationRange.max" controls-position="right" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="帧数">
                <el-input-number
                    v-model="createForm.frames"
                    :min="1"
                    :disabled="framesDisabled"
                    controls-position="right"
                    style="width: 100%">
                </el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="4">
              <el-form-item label="随机种子">
                <el-input-number v-model="createForm.seed" :min="0" controls-position="right" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="4">
              <el-form-item label="声音">
                <div class="audio-generate-control">
                  <el-switch v-model="createForm.generateAudio" disabled></el-switch>
                  <span class="audio-generate-text">生成</span>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>

        <aside class="media-preview-panel">
          <div class="media-preview-head">
            <span>素材预览</span>
            <span>{{ mediaPreviewItems.length }}</span>
          </div>
          <div v-if="mediaPreviewItems.length" class="media-preview-grid">
            <div v-for="item in mediaPreviewItems" :key="item.key" class="media-preview-item">
              <el-button
                  class="media-preview-delete"
                  type="danger"
                  size="mini"
                  icon="el-icon-delete"
                  circle
                  @click="removeMedia(item.index)">
              </el-button>
              <img
                  v-if="item.type === 'image' && item.previewUrl"
                  class="media-preview-thumb"
                  :src="item.previewUrl"
                  :alt="item.name">
              <video
                  v-else-if="item.type === 'video' && item.previewUrl"
                  class="media-preview-thumb"
                  :src="item.previewUrl"
                  controls
                  preload="metadata"
                  playsinline>
              </video>
              <audio
                  v-else-if="item.type === 'audio' && item.previewUrl"
                  class="media-preview-audio"
                  :src="item.previewUrl"
                  controls>
              </audio>
              <div v-else class="media-preview-empty">{{ mediaTypeLabel(item.type) }}</div>
              <div class="media-preview-meta">
                <span>{{ mediaTypeLabel(item.type) }}</span>
                <span>{{ roleLabel(item.role) }}</span>
              </div>
              <div class="media-preview-name" :title="item.name">{{ item.name }}</div>
            </div>
          </div>
          <div v-else class="media-preview-placeholder">暂无素材</div>
        </aside>
      </div>
    </section>

    <section class="panel billing-panel" v-loading="billingLoading">
      <div class="panel-title">
        <span>费用统计</span>
        <div class="billing-title-actions">
          <el-radio-group v-if="!billingCollapsed" v-model="billingGroup" size="mini" @change="handleBillingGroupChange">
            <el-radio-button label="PERIOD">周期</el-radio-button>
            <el-radio-button label="USER">用户</el-radio-button>
            <el-radio-button label="MODEL">模型</el-radio-button>
          </el-radio-group>
          <el-radio-group v-if="!billingCollapsed && billingGroup === 'PERIOD'" v-model="billingPeriod" size="mini" @change="handleBillingPeriodChange">
            <el-radio-button label="DAY">按日</el-radio-button>
            <el-radio-button label="WEEK">按周</el-radio-button>
            <el-radio-button label="MONTH">按月</el-radio-button>
          </el-radio-group>
          <el-button
              size="mini"
              plain
              :icon="billingCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"
              @click="toggleBillingCollapsed">
            {{ billingCollapsed ? '展开' : '收起' }}
          </el-button>
        </div>
      </div>

      <div v-show="!billingCollapsed">
        <div class="billing-summary-cards">
          <div class="billing-card">
            <span>估算费用</span>
            <strong>{{ formatCurrency(billingSummary.totalEstimatedCost) }}</strong>
          </div>
          <div class="billing-card">
            <span>Tokens</span>
            <strong>{{ formatInteger(billingSummary.totalUsageTokens) }}</strong>
          </div>
          <div class="billing-card">
            <span>计费任务</span>
            <strong>{{ formatInteger(billingSummary.chargedTaskCount) }} / {{ formatInteger(billingSummary.totalTaskCount) }}</strong>
          </div>
          <div class="billing-card">
            <span>视频总时长</span>
            <strong>{{ formatDuration(billingSummary.totalGeneratedDurationSeconds) }}</strong>
          </div>
        </div>

        <div v-show="billingGroup === 'PERIOD'" class="billing-sub-title">周期消耗</div>
        <el-table
            v-show="billingGroup === 'PERIOD'"
            :data="billingSummary.rows || []"
            size="mini"
            border
            stripe
            max-height="240"
            class="billing-table"
            :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
          <el-table-column prop="period" label="周期" width="140"></el-table-column>
          <el-table-column label="日期范围" min-width="180">
            <template slot-scope="{ row }">
              {{ row.periodStart === row.periodEnd ? row.periodStart : (row.periodStart + ' 至 ' + row.periodEnd) }}
            </template>
          </el-table-column>
          <el-table-column prop="taskCount" label="任务数" width="90" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.taskCount) }}</template>
          </el-table-column>
          <el-table-column prop="chargedTaskCount" label="计费任务" width="100" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.chargedTaskCount) }}</template>
          </el-table-column>
          <el-table-column prop="usageTokens" label="Tokens" width="140" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.usageTokens) }}</template>
          </el-table-column>
          <el-table-column prop="generatedDurationSeconds" label="视频时长" width="120" align="right">
            <template slot-scope="{ row }">{{ formatDuration(row.generatedDurationSeconds) }}</template>
          </el-table-column>
          <el-table-column prop="estimatedCost" label="估算费用" width="130" align="right">
            <template slot-scope="{ row }">{{ formatCurrency(row.estimatedCost) }}</template>
          </el-table-column>
        </el-table>

        <div v-show="billingGroup === 'USER'" class="billing-sub-title">用户消耗</div>
        <el-table
            v-show="billingGroup === 'USER'"
            :data="billingSummary.userRows || []"
            size="mini"
            border
            stripe
            max-height="220"
            class="billing-table"
            :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
          <el-table-column prop="createdBy" label="用户" min-width="140">
            <template slot-scope="{ row }">{{ row.createdBy || '-' }}</template>
          </el-table-column>
          <el-table-column prop="taskCount" label="任务数" width="90" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.taskCount) }}</template>
          </el-table-column>
          <el-table-column prop="chargedTaskCount" label="计费任务" width="100" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.chargedTaskCount) }}</template>
          </el-table-column>
          <el-table-column prop="usageTokens" label="Tokens" width="140" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.usageTokens) }}</template>
          </el-table-column>
          <el-table-column prop="generatedDurationSeconds" label="视频时长" width="120" align="right">
            <template slot-scope="{ row }">{{ formatDuration(row.generatedDurationSeconds) }}</template>
          </el-table-column>
          <el-table-column prop="estimatedCost" label="估算费用" width="130" align="right">
            <template slot-scope="{ row }">{{ formatCurrency(row.estimatedCost) }}</template>
          </el-table-column>
        </el-table>

        <div v-show="billingGroup === 'MODEL'" class="billing-sub-title">模型消耗</div>
        <el-table
            v-show="billingGroup === 'MODEL'"
            :data="billingSummary.modelRows || []"
            size="mini"
            border
            stripe
            max-height="300"
            class="billing-table"
            :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
          <el-table-column prop="model" label="模型" min-width="260" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <div class="task-model-cell" :title="modelLabel(row.model, row.provider)">
                <span class="task-model-name">{{ modelDisplayName(row.model, row.provider) }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="provider" label="服务商" width="110">
            <template slot-scope="{ row }">{{ providerLabel(row.provider) }}</template>
          </el-table-column>
          <el-table-column prop="taskCount" label="任务数" width="90" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.taskCount) }}</template>
          </el-table-column>
          <el-table-column prop="chargedTaskCount" label="计费任务" width="100" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.chargedTaskCount) }}</template>
          </el-table-column>
          <el-table-column prop="usageTokens" label="Tokens" width="140" align="right">
            <template slot-scope="{ row }">{{ formatInteger(row.usageTokens) }}</template>
          </el-table-column>
          <el-table-column prop="generatedDurationSeconds" label="视频时长" width="120" align="right">
            <template slot-scope="{ row }">{{ formatDuration(row.generatedDurationSeconds) }}</template>
          </el-table-column>
          <el-table-column prop="estimatedCost" label="估算费用" width="130" align="right">
            <template slot-scope="{ row }">{{ formatCurrency(row.estimatedCost) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </section>

    <section class="panel">
      <div class="panel-title">
        <span>任务列表</span>
        <el-form :inline="true" :model="query" size="mini" class="query-form">
          <el-form-item>
            <el-tag size="mini" type="info">{{ taskScopeLabel }}</el-tag>
          </el-form-item>
          <el-form-item label="服务商">
            <el-select v-model="query.provider" clearable placeholder="全部" style="width: 150px">
              <el-option
                  v-for="provider in queryProviderOptions"
                  :key="provider.value"
                  :label="provider.shortLabel"
                  :value="provider.value">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" clearable placeholder="全部" style="width: 130px">
              <el-option v-for="status in statusOptions" :key="status.value" :label="status.label" :value="status.value"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="日期">
            <el-date-picker
                v-model="query.dateRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                style="width: 240px">
            </el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-checkbox v-model="query.favoriteOnly">只看收藏</el-checkbox>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" :loading="taskLoading" @click="searchTasks">查询</el-button>
            <el-button
                icon="el-icon-refresh"
                :loading="batchSyncing"
                :disabled="selectedSyncTasks.length === 0"
                @click="syncSelectedTasks">同步选中</el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-table
          :data="tasks"
          size="mini"
          border
          stripe
          v-loading="taskLoading || batchSyncing"
          :height="tableHeight"
          @selection-change="handleTaskSelectionChange"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column type="selection" width="48" :selectable="canSelectTaskForSync"></el-table-column>
        <el-table-column label="收藏" width="70" align="center">
          <template slot-scope="{ row }">
            <el-button
                type="text"
                size="mini"
                :title="row.favorite ? '取消收藏' : '收藏'"
                :icon="row.favorite ? 'el-icon-star-on' : 'el-icon-star-off'"
                :class="{ 'favorite-active': row.favorite }"
                @click="toggleFavorite(row)">
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="createdBy" label="创建人" width="120">
          <template slot-scope="{ row }">{{ row.createdBy || '-' }}</template>
        </el-table-column>
        <el-table-column label="来源" width="120">
          <template slot-scope="{ row }">
            <el-tag v-if="isOwnTask(row)" size="mini" type="success">我的</el-tag>
            <el-tag v-else-if="row.sharedWithCurrentUser" size="mini" type="warning">{{ row.shareOwnerUsername || row.createdBy || '已分享' }}</el-tag>
            <el-tag v-else-if="row.groupVisible" size="mini" type="info">{{ row.groupName || '组内' }}</el-tag>
            <el-tag v-else size="mini" type="info">{{ row.createdBy || '其他用户' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="provider" label="服务商" width="110">
          <template slot-scope="{ row }">{{ providerLabel(row.provider) }}</template>
        </el-table-column>
        <el-table-column prop="providerKeyName" label="Key" width="130" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.providerKeyName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="sceneType" label="场景" width="140">
          <template slot-scope="{ row }">{{ sceneLabel(row.sceneType) }}</template>
        </el-table-column>
        <el-table-column prop="model" label="具体模型" min-width="220" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <div class="task-model-cell" :title="modelLabel(row.model, row.provider)">
              <span class="task-model-name">{{ modelDisplayName(row.model, row.provider) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Tokens / 费用" width="150" align="right" header-align="right">
          <template slot-scope="{ row }">
            <div class="billing-usage">{{ formatInteger(row.usageTokens) }}</div>
            <div class="billing-cost" :title="row.billingPricingRule || row.billingPricingNote">
              {{ formatCurrency(row.estimatedCost) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="prompt" label="提示词" min-width="260" show-overflow-tooltip></el-table-column>
        <el-table-column prop="resultUrl" label="成片地址" min-width="240">
          <template slot-scope="{ row }">
            <div v-if="row.resultUrl" class="result-video-cell">
              <video
                  class="result-video"
                  :src="publicUrl(row.resultUrl)"
                  :poster="publicUrl(row.coverUrl)"
                  controls
                  preload="metadata"
                  playsinline>
              </video>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="providerResultUrl" label="服务商结果" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link v-if="row.providerResultUrl" :href="publicUrl(row.providerResultUrl)" type="primary" target="_blank">打开</el-link>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="errorMessage" label="错误信息" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ friendlyTaskError(row.errorMessage) }}</template>
        </el-table-column>
        <el-table-column prop="recordCreatedAt" label="创建时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.recordCreatedAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="230" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" :disabled="!row.prompt" @click="continueWithPrompt(row)">继续</el-button>
            <el-button type="text" size="mini" :disabled="!isOwnTask(row) || isTerminal(row.status)" @click="syncTask(row)">同步</el-button>
            <el-button type="text" size="mini" class="danger-link" :disabled="!isOwnTask(row) || isTerminal(row.status)" @click="cancelTask(row)">取消</el-button>
            <el-button type="text" size="mini" @click="openEventDialog(row)">日志</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>共 {{ pagination.total }} 条</span>
        <el-pagination
            background
            layout="sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange">
        </el-pagination>
      </div>
    </section>
      </el-tab-pane>
    </el-tabs>

    <el-dialog
        title="保存参数预设"
        :visible.sync="creativePresetDialogVisible"
        width="420px"
        append-to-body
        :close-on-click-modal="false">
      <el-form size="mini" label-width="72px" @submit.native.prevent>
        <el-form-item label="名称" required>
          <el-input
              v-model.trim="creativePresetForm.name"
              maxlength="30"
              show-word-limit
              placeholder="例如：2.5 竖屏有声"
              @keyup.enter.native="saveCreativePreset">
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="mini" @click="creativePresetDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" @click="saveCreativePreset">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
        :title="eventDialogTitle"
        :visible.sync="eventDialogVisible"
        width="900px"
        :close-on-click-modal="false">
      <el-table
          :data="eventLogs"
          size="mini"
          border
          stripe
          v-loading="eventLoading"
          max-height="520"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column prop="recordCreatedAt" label="时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.recordCreatedAt) }}</template>
        </el-table-column>
        <el-table-column prop="eventType" label="事件" width="130">
          <template slot-scope="{ row }">{{ eventTypeLabel(row.eventType) }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="operator" label="操作人" width="120">
          <template slot-scope="{ row }">{{ row.operator || '-' }}</template>
        </el-table-column>
        <el-table-column prop="eventMessage" label="说明" min-width="220" show-overflow-tooltip></el-table-column>
        <el-table-column label="详情" width="80">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" :disabled="!row.eventPayload" @click="showEventPayload(row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog
        title="分享我的任务列表"
        :visible.sync="shareDialogVisible"
        width="560px"
        :close-on-click-modal="false">
      <el-form label-width="96px" size="mini">
        <el-form-item label="分享给">
          <el-select
              v-model="shareForm.usernames"
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="输入用户名后回车，可添加多个"
              style="width: 100%">
          </el-select>
        </el-form-item>
        <el-form-item>
          <span class="share-hint">保存后，对方可以看到你创建的全部 AI 视频任务；清空后保存即取消分享。</span>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="shareDialogVisible = false">取消</el-button>
        <el-button size="mini" type="primary" :loading="shareSaving" @click="saveShareSettings">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
        title="使用之前的提示词"
        :visible.sync="promptHistoryVisible"
        width="900px"
        :close-on-click-modal="false">
      <div class="picker-toolbar">
        <el-button size="mini" icon="el-icon-refresh" :loading="promptHistoryLoading" @click="loadPromptHistory">刷新</el-button>
      </div>
      <el-table
          :data="promptHistoryTasks"
          size="mini"
          border
          stripe
          v-loading="promptHistoryLoading"
          max-height="520"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column prop="prompt" label="提示词" min-width="320" show-overflow-tooltip></el-table-column>
        <el-table-column prop="provider" label="服务商" width="100">
          <template slot-scope="{ row }">{{ providerLabel(row.provider) }}</template>
        </el-table-column>
        <el-table-column prop="sceneType" label="场景" width="130">
          <template slot-scope="{ row }">{{ sceneLabel(row.sceneType) }}</template>
        </el-table-column>
        <el-table-column prop="model" label="模型" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="recordCreatedAt" label="创建时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.recordCreatedAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="continueWithPrompt(row)">继续</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog
        title="选择参考素材"
        :visible.sync="mediaPickerVisible"
        width="1180px"
        custom-class="media-picker-dialog"
        :close-on-click-modal="false"
        @closed="handleMediaPickerClosed">
      <el-tabs v-model="mediaPickerTab" @tab-click="handleMediaPickerTabChange">
        <el-tab-pane label="已上传素材" name="assets">
          <div class="picker-toolbar">
            <el-select v-model="mediaPickerQuery.mediaType" size="mini" clearable placeholder="全部类型" style="width: 140px" @change="loadReferenceMedia">
              <el-option label="图片" value="image"></el-option>
              <el-option label="视频" value="video"></el-option>
              <el-option label="音频" value="audio"></el-option>
            </el-select>
            <el-checkbox :value="true" disabled>只看我的</el-checkbox>
            <el-button size="mini" icon="el-icon-refresh" :loading="mediaAssetsLoading" @click="loadReferenceMedia">刷新</el-button>
            <el-button size="mini" type="primary" icon="el-icon-plus" :disabled="selectedMediaAssets.length === 0" @click="selectMediaAssets">加入选中</el-button>
          </div>
          <el-table
              :data="mediaAssets"
              size="mini"
              border
              stripe
              v-loading="mediaAssetsLoading"
              max-height="420"
              @selection-change="handleMediaAssetSelectionChange"
              :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
            <el-table-column type="selection" width="48" :selectable="canSelectMediaAsset"></el-table-column>
            <el-table-column prop="mediaType" label="类型" width="80">
              <template slot-scope="{ row }">{{ mediaTypeLabel(row.mediaType) }}</template>
            </el-table-column>
            <el-table-column prop="originalFileName" label="文件名" min-width="180" show-overflow-tooltip></el-table-column>
            <el-table-column prop="uploadedBy" label="上传人" width="120">
              <template slot-scope="{ row }">{{ row.uploadedBy || '-' }}</template>
            </el-table-column>
            <el-table-column label="预览" width="210">
              <template slot-scope="{ row }">
                <div class="picker-media-preview">
                  <video
                      v-if="mediaAssetType(row) === 'video' && row.accessUrl"
                      :src="mediaPreviewUrl(row.accessUrl)"
                      controls
                      preload="metadata"
                      playsinline
                      @click.stop>
                  </video>
                  <audio
                      v-else-if="mediaAssetType(row) === 'audio' && row.accessUrl"
                      :src="mediaPreviewUrl(row.accessUrl)"
                      controls
                      preload="metadata"
                      @click.stop>
                  </audio>
                  <img
                      v-else-if="mediaAssetType(row) === 'image' && row.accessUrl"
                      :src="mediaPreviewUrl(row.accessUrl)"
                      :alt="row.originalFileName || row.fileName || '参考图'">
                  <span v-else>-</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="accessUrl" label="URL" min-width="220" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <el-link v-if="row.accessUrl" :href="row.accessUrl" type="primary" target="_blank">打开</el-link>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="recordCreatedAt" label="上传时间" width="170">
              <template slot-scope="{ row }">{{ formatTime(row.recordCreatedAt) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="90" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" :disabled="!row.accessUrl" @click="selectMedia(row.accessUrl, row.mediaType, row.originalFileName)">选择</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="AI生成结果" name="generated">
          <div class="picker-toolbar">
            <el-checkbox :value="true" disabled>只看我的</el-checkbox>
            <el-button size="mini" icon="el-icon-refresh" :loading="generatedLoading" @click="loadGeneratedTasks">刷新</el-button>
            <el-button size="mini" type="primary" icon="el-icon-plus" :disabled="selectedGeneratedTasks.length === 0" @click="selectGeneratedVideos">加入选中视频</el-button>
          </div>
          <el-table
              :data="generatedTasks"
              size="mini"
              border
              stripe
              v-loading="generatedLoading"
              max-height="420"
              @selection-change="handleGeneratedSelectionChange"
              :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
            <el-table-column type="selection" width="48" :selectable="canSelectGeneratedVideo"></el-table-column>
            <el-table-column prop="taskNo" label="任务号" min-width="180" show-overflow-tooltip></el-table-column>
            <el-table-column prop="provider" label="服务商" width="110">
              <template slot-scope="{ row }">{{ providerLabel(row.provider) }}</template>
            </el-table-column>
            <el-table-column prop="prompt" label="提示词" min-width="220" show-overflow-tooltip></el-table-column>
            <el-table-column label="预览" width="210">
              <template slot-scope="{ row }">
                <div class="picker-media-preview">
                  <video
                      v-if="generatedVideoUrl(row)"
                      :src="generatedVideoUrl(row)"
                      :poster="publicUrl(row.coverUrl)"
                      controls
                      preload="metadata"
                      playsinline
                      @click.stop>
                  </video>
                  <span v-else>-</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="recordCreatedAt" label="生成时间" width="170">
              <template slot-scope="{ row }">{{ formatTime(row.recordCreatedAt) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="210" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" :disabled="!generatedVideoUrl(row)" @click="selectMedia(generatedVideoUrl(row), 'video', row.taskNo + '.mp4')">选视频</el-button>
                <el-button type="text" size="mini" :disabled="!row.lastFrameUrl" @click="selectMedia(row.lastFrameUrl, 'image', row.taskNo + '-尾帧')">选尾帧</el-button>
                <el-button type="text" size="mini" :disabled="!row.coverUrl" @click="selectMedia(row.coverUrl, 'image', row.taskNo + '-封面')">选封面</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="视频库" name="videoLibrary">
          <div class="picker-toolbar">
            <el-input
                v-model.trim="mediaPickerQuery.videoFileName"
                size="mini"
                clearable
                placeholder="文件名"
                style="width: 180px"
                @keyup.enter.native="loadUploadedVideos">
            </el-input>
            <el-checkbox :value="true" disabled>只看我的</el-checkbox>
            <el-button size="mini" icon="el-icon-refresh" :loading="uploadedVideoLoading" @click="loadUploadedVideos">刷新</el-button>
            <el-button size="mini" type="primary" icon="el-icon-plus" :disabled="selectedUploadedVideos.length === 0" @click="selectUploadedVideos">加入选中视频</el-button>
          </div>
          <el-table
              :data="uploadedVideos"
              size="mini"
              border
              stripe
              v-loading="uploadedVideoLoading"
              max-height="420"
              @selection-change="handleUploadedVideoSelectionChange"
              :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
            <el-table-column type="selection" width="48" :selectable="canSelectUploadedVideo"></el-table-column>
            <el-table-column prop="originalFileName" label="文件名" min-width="180" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.originalFileName || row.fileName || '-' }}</template>
            </el-table-column>
            <el-table-column prop="uploadPerson" label="上传人" width="120">
              <template slot-scope="{ row }">{{ row.uploadPerson || '-' }}</template>
            </el-table-column>
            <el-table-column prop="previewUrl" label="视频预览" width="210">
              <template slot-scope="{ row }">
                <div v-if="row.previewUrl" class="picker-media-preview">
                  <video
                      :src="publicUrl(row.previewUrl)"
                      :poster="publicUrl(row.videoCoverUrl)"
                      controls
                      preload="metadata"
                      playsinline
                      @click.stop>
                  </video>
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="videoCoverUrl" label="封面" min-width="160" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <el-link v-if="row.videoCoverUrl" :href="row.videoCoverUrl" type="primary" target="_blank">打开</el-link>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="recordCreatedAt" label="上传时间" width="170">
              <template slot-scope="{ row }">{{ formatTime(row.recordCreatedAt || row.createTime) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" :disabled="!row.previewUrl" @click="selectMedia(row.previewUrl, 'video', row.originalFileName || row.fileName || row.videoId || '视频素材')">选视频</el-button>
                <el-button type="text" size="mini" :disabled="!row.videoCoverUrl" @click="selectMedia(row.videoCoverUrl, 'image', (row.originalFileName || row.fileName || row.videoId || '视频素材') + '-封面')">选封面</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <el-dialog
        :title="previewTitle"
        :visible.sync="previewVisible"
        width="780px"
        :close-on-click-modal="false"
        @closed="closeVideoPreview">
      <video
          v-if="previewVisible && previewVideoUrl"
          class="preview-video"
          :src="previewVideoUrl"
          controls
          autoplay
          playsinline>
      </video>
      <div class="preview-actions">
        <el-link :href="previewVideoUrl" type="primary" target="_blank">新窗口打开</el-link>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  cancelAigcVideoTask,
  createAigcVideoTask,
  estimateAigcVideoBilling,
  getAigcVideoBillingSummary,
  listAigcProviderConfigs,
  listAigcVideoModelConfigs,
  listAigcReferenceMedia,
  listAigcVideoTaskShares,
  listAigcVideoTaskEvents,
  listAigcVideoTasks,
  listUploadedVideoRecords,
  saveAigcVideoTaskShares,
  setAigcVideoTaskFavorite,
  syncAigcVideoTask,
  uploadAigcReferenceMedia
} from '@/api/aigcVideo'

export default {
  name: 'AigcVideoGenerator',
  data() {
    return {
      activeMainTab: 'creative',
      creativeAdvancedOpen: false,
      creativePresets: this.initialCreativePresets(),
      selectedCreativePresetId: '',
      creativePresetDialogVisible: false,
      creativePresetForm: {
        name: ''
      },
      billingEstimateLoading: false,
      billingEstimateTimer: null,
      billingEstimateRequestId: 0,
      billingEstimate: {
        available: false,
        estimatedUsageTokens: 0,
        estimatedCost: 0,
        unitPricePerMillionTokens: null,
        baseUnitPricePerMillionTokens: null,
        discountFactor: null,
        pricingRule: '',
        pricingNote: ''
      },
      taskLoading: false,
      autoRefreshing: false,
      autoRefreshTimer: null,
      autoRefreshIntervalMs: 15000,
      batchSyncing: false,
      billingLoading: false,
      billingPeriod: 'DAY',
      billingGroup: this.initialBillingGroup(),
      billingCollapsed: this.initialBillingCollapsed(),
      billingSummary: {
        totalTaskCount: 0,
        chargedTaskCount: 0,
        totalUsageTokens: 0,
        totalGeneratedDurationSeconds: 0,
        totalEstimatedCost: 0,
        rows: [],
        userRows: [],
        modelRows: []
      },
      shareDialogVisible: false,
      shareLoading: false,
      shareSaving: false,
      shareForm: {
        usernames: []
      },
      providerConfigLoading: false,
      providerConfigs: [],
      videoModelConfigs: [],
      creating: false,
      createForm: this.defaultCreateForm(),
      createRules: {
        provider: [{ required: true, message: '请选择服务商', trigger: 'change' }],
        sceneType: [{ required: true, message: '请选择生成场景', trigger: 'change' }],
        model: [{ required: true, message: '请选择模型', trigger: 'change' }]
      },
      query: {
        provider: '',
        status: '',
        dateRange: [],
        favoriteOnly: false
      },
      tasks: [],
      selectedSyncTasks: [],
      eventDialogVisible: false,
      eventLoading: false,
      currentEventTask: null,
      eventLogs: [],
      promptHistoryVisible: false,
      promptHistoryLoading: false,
      promptHistoryTasks: [],
      continuedFromTask: null,
      promptMentionMenuOpen: false,
      promptMentionQuery: '',
      promptMentionStart: 0,
      promptMentionEnd: 0,
      promptMentionActiveIndex: 0,
      promptMentionCloseTimer: null,
      pendingMediaMention: null,
      promptMentionUploading: false,
      promptMentionFileDialogActive: false,
      nextMediaMentionId: 1,
      mediaPickerVisible: false,
      previewVisible: false,
      previewVideoUrl: '',
      previewTitle: '视频预览',
      mediaPickerTab: 'assets',
      mediaPickerTarget: null,
      mediaAssetsLoading: false,
      generatedLoading: false,
      uploadedVideoLoading: false,
      mediaAssets: [],
      generatedTasks: [],
      uploadedVideos: [],
      selectedMediaAssets: [],
      selectedGeneratedTasks: [],
      selectedUploadedVideos: [],
      mediaPickerQuery: {
        mediaType: '',
        videoFileName: ''
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      sceneOptions: [
        { value: 'TEXT_TO_VIDEO', label: '文字转视频', hint: '无需素材' },
        { value: 'IMAGE_TO_VIDEO', label: '图生视频', hint: '1 张首帧' },
        { value: 'IMAGE_PAIR_TO_VIDEO', label: '首尾帧视频', hint: '2 张图片' },
        { value: 'REFERENCE_TO_VIDEO', label: '参考图视频', hint: '图片 / 视频 / 音频' },
        { value: 'VIDEO_EDIT', label: '视频编辑', hint: '1 个视频' },
        { value: 'VIDEO_EXTEND', label: '视频续写', hint: '1 个视频' }
      ],
      modelOptions: {
        SEEDANCE: [
          { value: 'ep-20260810170955-pd9s7', label: 'Seedance 2.5 - ep-20260810170955-pd9s7' },
          { value: 'ep-20260730234646-7vjm5', label: 'Seedance 2.0 Fast - ep-20260730234646-7vjm5' }
        ],
        DASHSCOPE: {
          TEXT_TO_VIDEO: [
            { value: 'happyhorse-1.0-t2v', label: 'HappyHorse 文生视频 - happyhorse-1.0-t2v' }
          ],
          IMAGE_TO_VIDEO: [
            { value: 'happyhorse-1.0-i2v', label: 'HappyHorse 图生视频 - happyhorse-1.0-i2v' }
          ],
          IMAGE_PAIR_TO_VIDEO: [
            { value: 'happyhorse-1.0-i2v', label: 'HappyHorse 首尾帧视频 - happyhorse-1.0-i2v' }
          ],
          REFERENCE_TO_VIDEO: [
            { value: 'happyhorse-1.0-r2v', label: 'HappyHorse 参考图视频 - happyhorse-1.0-r2v' }
          ],
          VIDEO_EDIT: [
            { value: 'happyhorse-1.0-video-edit', label: 'HappyHorse 视频编辑 - happyhorse-1.0-video-edit' }
          ],
          VIDEO_EXTEND: [
            { value: 'happyhorse-1.0-video-edit', label: 'HappyHorse 视频续写 - happyhorse-1.0-video-edit' }
          ]
        }
      },
      statusOptions: [
        { value: 'CREATED', label: '已创建' },
        { value: 'SUBMITTED', label: '已提交' },
        { value: 'QUEUED', label: '排队中' },
        { value: 'RUNNING', label: '生成中' },
        { value: 'SUCCEEDED', label: '成功' },
        { value: 'FAILED', label: '失败' },
        { value: 'CANCELLED', label: '已取消' },
        { value: 'EXPIRED', label: '已过期' }
      ]
    }
  },
  computed: {
    videoProviderOptions() {
      const labels = {
        SEEDANCE: { label: '火山方舟 Seedance', shortLabel: 'Seedance' },
        DASHSCOPE: { label: '阿里 DashScope', shortLabel: 'DashScope' }
      }
      const configs = Array.isArray(this.providerConfigs) ? this.providerConfigs : []
      return ['SEEDANCE', 'DASHSCOPE']
          .filter(provider => {
            const config = configs.find(item => item && item.provider === provider)
            return this.providerHasUsableConfig(provider, config)
          })
          .map(provider => ({
            value: provider,
            label: labels[provider].label,
            shortLabel: labels[provider].shortLabel
          }))
    },
    queryProviderOptions() {
      return this.videoProviderOptions
    },
    currentProviderConfig() {
      return (this.providerConfigs || []).find(item => item && item.provider === this.createForm.provider) || null
    },
    showProviderKeySelect() {
      return this.createForm.provider === 'SEEDANCE'
    },
    currentProviderKeyOptions() {
      return this.providerKeyOptionsFor(this.createForm.provider, this.currentProviderConfig)
    },
    selectedProviderKeyOption() {
      const value = this.keyOptionValue(this.createForm.providerKeyId)
      return this.currentProviderKeyOptions.find(item => item.value === value) || null
    },
    selectedProviderKeyLabel() {
      const value = this.keyOptionValue(this.createForm.providerKeyId)
      const option = this.currentProviderKeyOptions.find(item => item.value === value)
      return option ? option.label : '默认 Key'
    },
    selectedVideoModelConfig() {
      return (this.videoModelConfigs || []).find(item => item && item.provider === this.createForm.provider && item.modelValue === this.createForm.model) || null
    },
    currentModelOptions() {
      if (!this.createForm.provider) {
        return []
      }
      const configuredOptions = this.configuredModelOptions(this.createForm.provider)
          .filter(model => this.modelConfigSupportsScene(model, this.createForm.sceneType))
          .filter(model => this.providerKeySupportsModel(this.selectedProviderKeyOption, model.modelValue))
      if (configuredOptions.length) {
        return configuredOptions.map(model => ({
          value: model.modelValue,
          label: model.displayName || model.modelValue,
          config: model
        }))
      }
      if (this.createForm.provider === 'DASHSCOPE') {
        const dashScopeOptions = this.modelOptions.DASHSCOPE || {}
        return dashScopeOptions[this.createForm.sceneType] || dashScopeOptions.TEXT_TO_VIDEO || []
      }
      return (this.modelOptions.SEEDANCE || [])
          .filter(model => this.seedanceModelSupportsScene(model.value, this.createForm.sceneType))
          .filter(model => this.providerKeySupportsModel(this.selectedProviderKeyOption, model.value))
    },
    currentResolutionOptions() {
      if (this.selectedVideoModelConfig && Array.isArray(this.selectedVideoModelConfig.resolutions) && this.selectedVideoModelConfig.resolutions.length) {
        return this.selectedVideoModelConfig.resolutions.map(item => ({ value: item, label: item }))
      }
      if (this.createForm.provider === 'SEEDANCE') {
        return [
          { value: '480p', label: '480p' }
        ]
      }
      return [
        { value: '1080p', label: '1080p' },
        { value: '720p', label: '720p' }
      ]
    },
    currentRatioOptions() {
      if (this.selectedVideoModelConfig && Array.isArray(this.selectedVideoModelConfig.ratios) && this.selectedVideoModelConfig.ratios.length) {
        return this.selectedVideoModelConfig.ratios.map(item => ({ value: item, label: item }))
      }
      const options = this.createForm.provider === 'SEEDANCE'
          ? [
            { value: '9:16', label: '9:16' }
          ]
          : [
            { value: '16:9', label: '16:9' },
            { value: '9:16', label: '9:16' },
            { value: '1:1', label: '1:1' },
            { value: '4:3', label: '4:3' },
            { value: '3:4', label: '3:4' }
          ]
      return this.ratioAdaptiveSupported()
          ? [{ value: 'adaptive', label: '自适应' }].concat(options)
          : options
    },
    currentDurationRange() {
      if (this.selectedVideoModelConfig) {
        return {
          min: this.selectedVideoModelConfig.minDuration || 1,
          max: this.selectedVideoModelConfig.maxDuration || 30
        }
      }
      if (this.isSeedance25Model()) {
        return { min: 4, max: 30 }
      }
      if (this.isSeedance2Model()) {
        return { min: 4, max: 15 }
      }
      if (this.isSeedance15Model()) {
        return { min: 4, max: 12 }
      }
      if (this.createForm.provider === 'SEEDANCE') {
        return { min: 2, max: 12 }
      }
      return { min: 1, max: 30 }
    },
    framesDisabled() {
      if (this.selectedVideoModelConfig && this.selectedVideoModelConfig.framesDisabled !== undefined && this.selectedVideoModelConfig.framesDisabled !== null) {
        return this.selectedVideoModelConfig.framesDisabled
      }
      return this.isSeedance25Model() || this.isSeedance2Model() || this.isSeedance15Model()
    },
    generateAudioDisabled() {
      if (this.selectedVideoModelConfig && this.selectedVideoModelConfig.supportsAudio === false) {
        return true
      }
      return this.createForm.provider === 'SEEDANCE' && !this.seedanceGenerateAudioSupported()
    },
    tableHeight() {
      return 'calc(100vh - 430px)'
    },
    taskScopeLabel() {
      return this.currentUserCanViewAllTasks() ? '全部任务' : '我的、组内和分享给我的'
    },
    eventDialogTitle() {
      const task = this.currentEventTask || {}
      return task.taskNo ? `任务日志：${task.taskNo}` : '任务日志'
    },
    promptMentionEntries() {
      const query = String(this.promptMentionQuery || '').trim().toLowerCase()
      const mediaEntries = this.mediaPreviewItems
          .filter(item => {
            if (!query) return true
            const keywords = [item.name, this.roleLabel(item.role), this.mediaTypeLabel(item.type)]
                .join(' ')
                .toLowerCase()
            return keywords.includes(query)
          })
          .map(item => ({
            key: `media-${item.key}`,
            kind: 'media',
            item
          }))
      const addEntries = ['image', 'video', 'audio'].map(mediaType => ({
        key: `add-${mediaType}`,
        kind: 'add',
        mediaType
      }))
      return mediaEntries.concat([{ key: 'upload-local', kind: 'upload' }], addEntries)
    },
    mediaPreviewItems() {
      return (this.createForm.media || [])
          .map((item, index) => {
            if (!item || !String(item.url || '').trim()) {
              return null
            }
            const url = String(item.url || '').trim()
            const type = this.inferMediaTypeFromUrl(url, item.type)
            return {
              key: index + '-' + url,
              index,
              type,
              role: item.role,
              name: item.fileName || this.mediaNameFromUrl(url) || `素材 ${index + 1}`,
              previewUrl: this.mediaPreviewUrl(url)
            }
          })
          .filter(item => item && (item.previewUrl || item.name))
    },
    recentCreativeResults() {
      return (this.tasks || [])
          .filter(Boolean)
          .slice(0, 5)
    },
    billingEstimateKey() {
      return JSON.stringify({
        provider: this.createForm.provider,
        sceneType: this.createForm.sceneType,
        model: this.createForm.model,
        providerKeyId: this.createForm.providerKeyId,
        resolution: this.createForm.resolution,
        duration: this.createForm.duration,
        generateAudio: this.createForm.generateAudio,
        media: (this.createForm.media || [])
            .filter(item => item && item.url)
            .map(item => ({ type: item.type, role: item.role }))
      })
    }
  },
  watch: {
    billingEstimateKey() {
      this.scheduleBillingEstimate()
    },
    activeMainTab(value) {
      if (value !== 'creative') {
        this.closePromptMentionMenu()
      }
    }
  },
  async mounted() {
    await this.ensureCurrentUserProfile()
    await this.loadVideoProviderConfigs()
    await this.loadVideoModelConfigs()
    this.loadTasks()
    this.loadBillingSummary()
    this.scheduleBillingEstimate()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    if (this.billingEstimateTimer) {
      window.clearTimeout(this.billingEstimateTimer)
      this.billingEstimateTimer = null
    }
    if (this.promptMentionCloseTimer) {
      window.clearTimeout(this.promptMentionCloseTimer)
      this.promptMentionCloseTimer = null
    }
  },
  methods: {
    providerHasUsableConfig(provider, config) {
      if (!config || config.enabled === false) {
        return false
      }
      if (provider === 'SEEDANCE') {
        return this.providerKeyOptionsFor(provider, config).length > 0
      }
      return config.apiKeyConfigured !== false
    },
    providerKeyOptionsFor(provider, config) {
      if (provider !== 'SEEDANCE') {
        return []
      }
      const providerConfig = config || (this.providerConfigs || []).find(item => item && item.provider === provider)
      if (!providerConfig || providerConfig.enabled === false) {
        return []
      }
      return (providerConfig.keyConfigs || [])
          .filter(item => item && item.enabled !== false && item.apiKeyConfigured !== false)
          .map((item, index) => {
            const value = item.legacyKey ? '__legacy__' : this.keyOptionValue(item.id)
            const name = item.keyName || (item.id ? `Key ${index + 1}` : '默认 Key')
            return {
              optionKey: item.id ? `key-${item.id}` : `legacy-${index}`,
              value,
              label: item.defaultKey ? `${name}（默认）` : name,
              apiKeyPreview: item.apiKeyPreview || '',
              legacyKey: item.legacyKey === true,
              supportedModels: item.supportedModels || [],
              defaultKey: item.defaultKey === true
            }
          })
    },
    keyOptionValue(value) {
      if (value === undefined || value === null || value === '') {
        return ''
      }
      return String(value)
    },
    normalizedProviderKeyId() {
      if (!this.showProviderKeySelect) {
        return null
      }
      const value = this.keyOptionValue(this.createForm.providerKeyId)
      if (!value || value === '__legacy__') {
        return null
      }
      const numericValue = Number(value)
      return Number.isNaN(numericValue) ? null : numericValue
    },
    selectedProviderKeyLegacy() {
      return this.showProviderKeySelect && this.keyOptionValue(this.createForm.providerKeyId) === '__legacy__'
    },
    providerKeySupportsModel(keyOption, modelValue) {
      const supportedModels = keyOption && Array.isArray(keyOption.supportedModels) ? keyOption.supportedModels : []
      if (!supportedModels.length || !modelValue) {
        return true
      }
      return supportedModels.includes(modelValue)
    },
    syncProviderKeySelection() {
      if (!this.showProviderKeySelect) {
        this.createForm.providerKeyId = ''
        return
      }
      const options = this.currentProviderKeyOptions
      if (!options.length) {
        this.createForm.providerKeyId = ''
        return
      }
      const currentValue = this.keyOptionValue(this.createForm.providerKeyId)
      if (!options.some(item => item.value === currentValue)) {
        const preferred = options.find(item => item.defaultKey) || options[0]
        this.createForm.providerKeyId = preferred.value
      }
    },
    configuredModelOptions(provider) {
      return (this.videoModelConfigs || [])
          .filter(item => item && item.provider === provider && item.enabled !== false && item.modelValue)
          .slice()
          .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    },
    modelConfigSupportsScene(model, sceneType) {
      const scenes = model && Array.isArray(model.sceneTypes) ? model.sceneTypes : []
      return !scenes.length || scenes.includes(sceneType)
    },
    creativePresetStorageKey() {
      let username = 'default'
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        username = String(user.username || user.id || 'default')
      } catch (e) {
        username = 'default'
      }
      return `aigcVideoCreativePresets:${username}`
    },
    initialCreativePresets() {
      try {
        const presets = JSON.parse(localStorage.getItem(this.creativePresetStorageKey()) || '[]')
        return Array.isArray(presets) ? presets.filter(item => item && item.id && item.name).slice(0, 20) : []
      } catch (e) {
        return []
      }
    },
    persistCreativePresets() {
      try {
        localStorage.setItem(this.creativePresetStorageKey(), JSON.stringify(this.creativePresets.slice(0, 20)))
        return true
      } catch (e) {
        this.$message.error('参数预设保存失败，请检查浏览器存储权限')
        return false
      }
    },
    async loadVideoProviderConfigs() {
      this.providerConfigLoading = true
      try {
        const response = await listAigcProviderConfigs()
        this.providerConfigs = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.providerConfigs = []
        this.$message.error(error.message || '获取服务商配置失败')
      } finally {
        this.providerConfigLoading = false
        this.syncVideoProviderSelection()
      }
    },
    async loadVideoModelConfigs() {
      try {
        const response = await listAigcVideoModelConfigs('SEEDANCE')
        this.videoModelConfigs = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.videoModelConfigs = []
        this.$message.error(error.message || '获取视频模型配置失败')
      } finally {
        this.syncVideoProviderSelection()
      }
    },
    syncVideoProviderSelection() {
      const providers = this.videoProviderOptions
      if (!providers.length) {
        this.createForm.provider = ''
        this.createForm.model = ''
        this.createForm.providerKeyId = ''
        this.query.provider = ''
        this.createForm.generateAudio = true
        return
      }
      if (this.query.provider && !providers.some(item => item.value === this.query.provider)) {
        this.query.provider = ''
      }
      if (!providers.some(item => item.value === this.createForm.provider)) {
        this.createForm.provider = providers[0].value
      }
      this.createForm.generateAudio = true
      this.syncProviderKeySelection()
      this.applyRecommendedModel(true)
      this.applyModelConstraints()
      this.normalizeCreateMediaRoles()
    },
    openCreativePresetDialog() {
      this.creativePresetForm.name = [
        this.modelDisplayName(this.createForm.model, this.createForm.provider),
        this.sceneLabel(this.createForm.sceneType),
        this.createForm.ratio
      ].filter(Boolean).join(' · ').slice(0, 30)
      this.creativePresetDialogVisible = true
    },
    saveCreativePreset() {
      const name = String(this.creativePresetForm.name || '').trim()
      if (!name) {
        this.$message.warning('请输入预设名称')
        return
      }
      const settings = {
        provider: this.createForm.provider,
        providerKeyId: this.createForm.providerKeyId,
        sceneType: this.createForm.sceneType,
        model: this.createForm.model,
        resolution: this.createForm.resolution,
        ratio: this.createForm.ratio,
        duration: this.createForm.duration,
        frames: this.createForm.frames,
        seed: this.createForm.seed,
        generateAudio: this.createForm.generateAudio
      }
      const sameNameIndex = this.creativePresets.findIndex(item => item.name === name)
      const preset = {
        id: sameNameIndex >= 0 ? this.creativePresets[sameNameIndex].id : `preset-${Date.now()}`,
        name,
        settings,
        updatedAt: Date.now()
      }
      if (sameNameIndex >= 0) {
        this.$set(this.creativePresets, sameNameIndex, preset)
      } else {
        this.creativePresets.unshift(preset)
      }
      this.creativePresets = this.creativePresets
          .slice()
          .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0))
          .slice(0, 20)
      if (!this.persistCreativePresets()) {
        return
      }
      this.selectedCreativePresetId = preset.id
      this.creativePresetDialogVisible = false
      this.$message.success(sameNameIndex >= 0 ? '参数预设已更新' : '参数预设已保存')
    },
    applyCreativePreset(presetId) {
      if (!presetId) {
        return
      }
      const preset = this.creativePresets.find(item => item.id === presetId)
      if (!preset || !preset.settings) {
        return
      }
      const settings = preset.settings
      this.createForm.provider = settings.provider || this.createForm.provider
      if (Object.prototype.hasOwnProperty.call(settings, 'providerKeyId')) {
        this.createForm.providerKeyId = this.keyOptionValue(settings.providerKeyId)
      }
      this.createForm.sceneType = settings.sceneType || this.createForm.sceneType
      this.createForm.model = settings.model || this.createForm.model
      const settingKeys = ['resolution', 'ratio', 'duration', 'frames', 'seed', 'generateAudio']
      settingKeys.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(settings, key)) {
          this.$set(this.createForm, key, settings[key])
        }
      })
      if (!this.currentModelOptions.some(item => item.value === this.createForm.model)) {
        this.applyRecommendedModel(true)
      }
      this.syncProviderKeySelection()
      this.applyModelConstraints()
      this.normalizeCreateMediaRoles()
      this.$message.success(`已应用“${preset.name}”`)
    },
    async deleteCreativePreset() {
      const preset = this.creativePresets.find(item => item.id === this.selectedCreativePresetId)
      if (!preset) {
        return
      }
      try {
        await this.$confirm(`确定删除参数预设“${preset.name}”吗？`, '删除预设', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      this.creativePresets = this.creativePresets.filter(item => item.id !== preset.id)
      this.selectedCreativePresetId = ''
      this.persistCreativePresets()
      this.$message.success('参数预设已删除')
    },
    scheduleBillingEstimate() {
      if (this.billingEstimateTimer) {
        window.clearTimeout(this.billingEstimateTimer)
      }
      this.billingEstimateTimer = window.setTimeout(() => {
        this.billingEstimateTimer = null
        this.loadBillingEstimate()
      }, 350)
    },
    async loadBillingEstimate() {
      if (!this.createForm.provider || !this.createForm.model || !this.createForm.duration) {
        this.billingEstimate = {
          available: false,
          pricingNote: '完善模型参数后显示'
        }
        return
      }
      const requestId = this.billingEstimateRequestId + 1
      this.billingEstimateRequestId = requestId
      this.billingEstimateLoading = true
      try {
        const response = await estimateAigcVideoBilling(this.buildBillingEstimatePayload())
        if (requestId === this.billingEstimateRequestId) {
          this.billingEstimate = Object.assign({
            available: false,
            estimatedUsageTokens: 0,
            estimatedCost: 0,
            unitPricePerMillionTokens: null,
            baseUnitPricePerMillionTokens: null,
            discountFactor: null,
            pricingRule: '',
            pricingNote: ''
          }, response.data || {})
        }
      } catch (error) {
        if (requestId === this.billingEstimateRequestId) {
          this.billingEstimate = {
            available: false,
            pricingNote: error.message || '费用预估暂不可用'
          }
        }
      } finally {
        if (requestId === this.billingEstimateRequestId) {
          this.billingEstimateLoading = false
        }
      }
    },
    initialBillingCollapsed() {
      try {
        return localStorage.getItem('aigcVideoBillingCollapsed') === 'true'
      } catch (e) {
        return false
      }
    },
    initialBillingGroup() {
      try {
        const value = localStorage.getItem('aigcVideoBillingGroup')
        return ['PERIOD', 'USER', 'MODEL'].includes(value) ? value : 'PERIOD'
      } catch (e) {
        return 'PERIOD'
      }
    },
    async ensureCurrentUserProfile() {
      if (this.currentAccountType()) {
        return
      }
      const username = this.currentUsername()
      if (!username || username === 'unknown') {
        return
      }
      try {
        const response = await this.$axios.get('/permission/users', {
          baseURL: 'https://dy.darknight.games/api'
        })
        const payload = response && response.data ? response.data : response
        const users = Array.isArray(payload.data) ? payload.data : []
        const currentUser = users.find(user => String(user.username || '') === username)
        if (currentUser) {
          this.saveCurrentUserProfile(currentUser)
        }
      } catch (e) {
        // 权限信息兜底失败时，继续按当前缓存身份查询任务。
      }
    },
    saveCurrentUserProfile(profile) {
      if (!profile) {
        return
      }
      const localUser = this.localStoredUser()
      const accountType = this.firstDefined(
          profile.accountType,
          profile.account_type,
          profile.userType,
          profile.user_type,
          profile.type
      )
      const nextUser = Object.assign({}, localUser, profile, {
        username: this.firstDefined(profile.username, localUser.username, this.currentUsername())
      })
      if (accountType !== undefined) {
        nextUser.accountType = accountType
        nextUser.account_type = accountType
      }
      const userId = this.firstDefined(profile.id, profile.userId, profile.user_id, localUser.id, localUser.userId)
      if (userId !== undefined) {
        nextUser.id = userId
        nextUser.userId = userId
      }
      localStorage.setItem('user', JSON.stringify(nextUser))
      if (this.$store && this.$store.commit) {
        this.$store.commit('login', nextUser)
      }
    },
    async openShareDialog() {
      this.shareDialogVisible = true
      this.shareLoading = true
      try {
        const response = await listAigcVideoTaskShares()
        const shares = response.data || []
        this.shareForm.usernames = shares
            .map(item => item.sharedUsername)
            .filter(Boolean)
      } catch (error) {
        this.shareForm.usernames = []
        this.$message.error(error.message || '获取分享设置失败')
      } finally {
        this.shareLoading = false
      }
    },
    async saveShareSettings() {
      this.shareSaving = true
      try {
        const usernames = Array.from(new Set((this.shareForm.usernames || [])
            .map(item => String(item || '').trim())
            .filter(item => item && item !== this.currentUsername())))
        const response = await saveAigcVideoTaskShares({ usernames })
        const shares = response.data || []
        this.shareForm.usernames = shares
            .map(item => item.sharedUsername)
            .filter(Boolean)
        this.$message.success('分享设置已保存')
        this.shareDialogVisible = false
      } catch (error) {
        this.$message.error(error.message || '保存分享设置失败')
      } finally {
        this.shareSaving = false
      }
    },
    defaultCreateForm() {
      return {
        provider: 'SEEDANCE',
        providerKeyId: '',
        sceneType: 'TEXT_TO_VIDEO',
        model: 'ep-20260810170955-pd9s7',
        prompt: '',
        media: [],
        resolution: '480p',
        ratio: '9:16',
        duration: 5,
        frames: undefined,
        seed: undefined,
        generateAudio: true
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.autoRefreshTimer = window.setInterval(() => {
        this.autoRefreshTasks()
      }, this.autoRefreshIntervalMs)
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        window.clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },
    canAutoRefreshTasks() {
      return !this.taskLoading
          && !this.autoRefreshing
          && !this.creating
          && !this.batchSyncing
          && this.selectedSyncTasks.length === 0
          && !this.eventDialogVisible
          && !this.promptHistoryVisible
          && !this.mediaPickerVisible
          && !this.previewVisible
    },
    async autoRefreshTasks() {
      if (!this.canAutoRefreshTasks()) {
        return
      }
      this.autoRefreshing = true
      try {
        await this.loadTasks({ silent: true, background: true })
      } finally {
        this.autoRefreshing = false
      }
    },
    handleProviderChange() {
      this.createForm.generateAudio = true
      this.syncProviderKeySelection()
      this.applyRecommendedModel(true)
      this.applyModelConstraints()
      this.normalizeCreateMediaRoles()
    },
    handleProviderKeyChange() {
      this.applyRecommendedModel(true)
      this.applyModelConstraints()
      this.scheduleBillingEstimate()
    },
    handleSceneChange() {
      this.createForm.generateAudio = true
      this.applyRecommendedModel(false)
      this.applyModelConstraints()
      this.normalizeCreateMediaRoles()
    },
    handleModelChange() {
      this.createForm.generateAudio = true
      this.applyModelConstraints()
      this.normalizeCreateMediaRoles()
    },
    applyRecommendedModel(force) {
      const recommended = this.recommendedModel()
      if (!recommended) {
        return
      }
      const knownValues = this.currentModelOptions.map(item => item.value)
      if (force || !this.createForm.model || !knownValues.includes(this.createForm.model)) {
        this.createForm.model = recommended
      }
    },
    recommendedModel() {
      const options = this.currentModelOptions
      return options.length > 0 ? options[0].value : ''
    },
    applyModelConstraints() {
      const allowedResolutions = this.currentResolutionOptions.map(item => item.value)
      if (!this.createForm.resolution || !allowedResolutions.includes(this.createForm.resolution)) {
        const preferredResolution = this.selectedVideoModelConfig && this.selectedVideoModelConfig.defaultResolution
        this.createForm.resolution = preferredResolution && allowedResolutions.includes(preferredResolution)
            ? preferredResolution
            : (allowedResolutions.includes('720p') ? '720p' : allowedResolutions[0])
      }
      const allowedRatios = this.currentRatioOptions.map(item => item.value)
      if (!this.createForm.ratio || !allowedRatios.includes(this.createForm.ratio)) {
        const preferredRatio = this.selectedVideoModelConfig && this.selectedVideoModelConfig.defaultRatio
        this.createForm.ratio = preferredRatio && allowedRatios.includes(preferredRatio)
            ? preferredRatio
            : (allowedRatios.includes('16:9') ? '16:9' : allowedRatios[0])
      }
      const range = this.currentDurationRange
      if (this.createForm.duration && this.createForm.duration !== -1) {
        if (this.createForm.duration < range.min) {
          this.createForm.duration = range.min
        } else if (this.createForm.duration > range.max) {
          this.createForm.duration = range.max
        }
      }
      if (this.framesDisabled) {
        this.createForm.frames = undefined
      }
      if (this.selectedVideoModelConfig && this.selectedVideoModelConfig.forceAudio === true) {
        this.createForm.generateAudio = true
      } else if (this.selectedVideoModelConfig && this.selectedVideoModelConfig.supportsAudio === false) {
        this.createForm.generateAudio = false
      } else {
        this.createForm.generateAudio = true
      }
    },
    isSeedance2FastModel() {
      return this.createForm.provider === 'SEEDANCE' && this.seedanceModelKind(this.createForm.model) === 'seedance20fast'
    },
    isSeedance25Model() {
      return this.createForm.provider === 'SEEDANCE' && this.seedanceModelKind(this.createForm.model) === 'seedance25'
    },
    isSeedance2Model() {
      return this.createForm.provider === 'SEEDANCE' && ['seedance20', 'seedance20fast'].includes(this.seedanceModelKind(this.createForm.model))
    },
    isSeedance15Model() {
      return this.createForm.provider === 'SEEDANCE' && this.seedanceModelKind(this.createForm.model) === 'seedance15pro'
    },
    isSeedance10LiteModel() {
      return ['seedance10litei2v', 'seedance10litet2v'].includes(this.seedanceModelKind(this.createForm.model))
    },
    seedanceModelKind(modelValue) {
      const model = String(modelValue || '').toLowerCase()
      if (model === 'ep-20260810170955-pd9s7') {
        return 'seedance25'
      }
      if (model === 'ep-20260730234646-7vjm5' || model.indexOf('seedance-2-0-fast') >= 0) {
        return 'seedance20fast'
      }
      if (model === 'ep-20260730234622-qvn6z' || model.indexOf('seedance-2-0') >= 0) {
        return 'seedance20'
      }
      if (model.indexOf('seedance-1-5-pro') >= 0) {
        return 'seedance15pro'
      }
      if (model.indexOf('seedance-1-0-pro-fast') >= 0) {
        return 'seedance10profast'
      }
      if (model.indexOf('seedance-1-0-pro') >= 0) {
        return 'seedance10pro'
      }
      if (model.indexOf('seedance-1-0-lite-i2v') >= 0) {
        return 'seedance10litei2v'
      }
      if (model.indexOf('seedance-1-0-lite-t2v') >= 0) {
        return 'seedance10litet2v'
      }
      return ''
    },
    seedanceModelSupportsScene(modelValue, sceneType) {
      const kind = this.seedanceModelKind(modelValue)
      if (!kind || ['seedance25', 'seedance20', 'seedance20fast'].includes(kind)) {
        return true
      }
      if (kind === 'seedance15pro' || kind === 'seedance10pro') {
        return ['TEXT_TO_VIDEO', 'IMAGE_TO_VIDEO', 'IMAGE_PAIR_TO_VIDEO'].includes(sceneType)
      }
      if (kind === 'seedance10profast') {
        return ['TEXT_TO_VIDEO', 'IMAGE_TO_VIDEO'].includes(sceneType)
      }
      if (kind === 'seedance10litei2v') {
        return ['IMAGE_TO_VIDEO', 'IMAGE_PAIR_TO_VIDEO', 'REFERENCE_TO_VIDEO'].includes(sceneType)
      }
      if (kind === 'seedance10litet2v') {
        return sceneType === 'TEXT_TO_VIDEO'
      }
      return true
    },
    seedanceGenerateAudioSupported() {
      return ['seedance25', 'seedance20', 'seedance20fast', 'seedance15pro'].includes(this.seedanceModelKind(this.createForm.model))
    },
    ratioAdaptiveSupported() {
      if (this.createForm.provider !== 'SEEDANCE') {
        return false
      }
      const kind = this.seedanceModelKind(this.createForm.model)
      if (['seedance25', 'seedance20', 'seedance20fast', 'seedance15pro'].includes(kind)) {
        return true
      }
      if (this.createForm.sceneType === 'REFERENCE_TO_VIDEO' && kind === 'seedance10litei2v') {
        return false
      }
      return ['IMAGE_TO_VIDEO', 'IMAGE_PAIR_TO_VIDEO'].includes(this.createForm.sceneType)
    },
    setCreativeScene(sceneType) {
      if (this.createForm.sceneType === sceneType) {
        return
      }
      this.createForm.sceneType = sceneType
      this.handleSceneChange()
    },
    sceneIcon(sceneType) {
      const map = {
        TEXT_TO_VIDEO: 'el-icon-edit-outline',
        IMAGE_TO_VIDEO: 'el-icon-picture-outline',
        IMAGE_PAIR_TO_VIDEO: 'el-icon-copy-document',
        REFERENCE_TO_VIDEO: 'el-icon-collection',
        VIDEO_EDIT: 'el-icon-magic-stick',
        VIDEO_EXTEND: 'el-icon-right'
      }
      return map[sceneType] || 'el-icon-video-camera'
    },
    openCreativeMediaPicker(type) {
      const item = this.appendEmptyMedia(type || 'image')
      this.openMediaPicker(item)
    },
    handleMediaPickerClosed() {
      if (this.mediaPickerTarget && !String(this.mediaPickerTarget.url || '').trim()) {
        const index = this.createForm.media.indexOf(this.mediaPickerTarget)
        if (index >= 0) {
          this.createForm.media.splice(index, 1)
        }
      }
      if (this.pendingMediaMention) {
        this.removePendingMentionTrigger(this.pendingMediaMention)
        this.pendingMediaMention = null
      }
      this.mediaPickerTarget = null
      this.selectedMediaAssets = []
      this.selectedGeneratedTasks = []
      this.selectedUploadedVideos = []
    },
    uploadCreativeMedia(request, preferredType) {
      const file = request.file || {}
      const type = file.aigcMediaType || this.detectUploadMediaType(file) || preferredType || 'image'
      const item = this.appendEmptyMedia(type)
      return this.uploadMedia(request, item)
    },
    addMedia() {
      const item = {
        type: 'image',
        role: '',
        url: '',
        fileName: '',
        uploading: false
      }
      item.role = this.defaultMediaRole(item.type, item)
      this.createForm.media.push(item)
    },
    removeMedia(index) {
      const item = this.createForm.media[index]
      this.removePromptMediaMention(item)
      this.createForm.media.splice(index, 1)
    },
    handleMediaTypeChange(item) {
      if (!item) {
        return
      }
      const type = item.type || 'image'
      if (!this.keepMediaRole(type, item.role)) {
        this.$set(item, 'role', this.defaultMediaRole(type, item))
      }
    },
    normalizeCreateMediaRoles() {
      (this.createForm.media || []).forEach(item => this.handleMediaTypeChange(item))
    },
    openMediaPicker(item) {
      this.mediaPickerTarget = item
      this.mediaPickerTab = 'assets'
      this.mediaPickerQuery.mediaType = item.type || ''
      this.mediaPickerVisible = true
      this.loadReferenceMedia()
    },
    handleMediaPickerTabChange() {
      if (this.mediaPickerTab === 'assets') {
        this.loadReferenceMedia()
      } else if (this.mediaPickerTab === 'generated') {
        this.loadGeneratedTasks()
      } else if (this.mediaPickerTab === 'videoLibrary') {
        this.loadUploadedVideos()
      }
    },
    async loadReferenceMedia() {
      this.mediaAssetsLoading = true
      try {
        const response = await listAigcReferenceMedia({
          mediaType: this.mediaPickerQuery.mediaType,
          uploadedBy: this.currentUsername(),
          page: 0,
          size: 50
        })
        const pageData = response.data || {}
        this.mediaAssets = pageData.content || []
        this.selectedMediaAssets = []
      } catch (error) {
        this.mediaAssets = []
        this.$message.error(error.message || '获取已上传素材失败')
      } finally {
        this.mediaAssetsLoading = false
      }
    },
    async loadGeneratedTasks() {
      this.generatedLoading = true
      try {
        const response = await listAigcVideoTasks({
          status: 'SUCCEEDED',
          createdBy: this.currentUsername(),
          page: 0,
          size: 50
        })
        const pageData = response.data || {}
        this.generatedTasks = (pageData.content || []).filter(item => item.resultUrl || item.providerResultUrl || item.lastFrameUrl || item.coverUrl)
        this.selectedGeneratedTasks = []
      } catch (error) {
        this.generatedTasks = []
        this.$message.error(error.message || '获取 AI 生成结果失败')
      } finally {
        this.generatedLoading = false
      }
    },
    publicUrl(url) {
      const value = String(url || '').trim()
      if (!value) {
        return ''
      }
      if (/^(https?:)?\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
        return value
      }
      if (value.startsWith('/')) {
        return value
      }
      return 'https://' + value
    },
    mediaPreviewUrl(url) {
      const value = String(url || '').trim()
      if (!value || /^cos:\/\//i.test(value)) {
        return ''
      }
      return this.publicUrl(value)
    },
    inferMediaTypeFromUrl(url, fallbackType) {
      const value = String(url || '').split('?')[0].toLowerCase()
      if (/\.(jpg|jpeg|png|webp|gif)$/.test(value)) {
        return 'image'
      }
      if (/\.(mp4|mov|m4v|webm|avi|mkv)$/.test(value)) {
        return 'video'
      }
      if (/\.(mp3|wav|m4a|aac|ogg|flac)$/.test(value)) {
        return 'audio'
      }
      return ['image', 'video', 'audio'].includes(fallbackType) ? fallbackType : 'image'
    },
    mediaNameFromUrl(url) {
      const value = String(url || '').trim()
      if (!value) {
        return ''
      }
      const path = value.split('?')[0].replace(/\\/g, '/')
      const name = path.substring(path.lastIndexOf('/') + 1)
      return name || value
    },
    openVideoPreview(row, url) {
      const previewUrl = this.publicUrl(url)
      if (!previewUrl) {
        this.$message.warning('暂无可预览的视频地址')
        return
      }
      this.previewVideoUrl = previewUrl
      this.previewTitle = row && row.taskNo ? `视频预览：${row.taskNo}` : '视频预览'
      this.previewVisible = true
    },
    creativeResultUrl(row) {
      return this.publicUrl((row && (row.resultUrl || row.providerResultUrl)) || '')
    },
    downloadCreativeResult(row) {
      const resultUrl = this.creativeResultUrl(row)
      if (!resultUrl) {
        this.$message.warning('暂无可下载的视频')
        return
      }
      const separator = resultUrl.includes('?') ? '&' : '?'
      const downloadUrl = /[?&]download=/i.test(resultUrl)
          ? resultUrl
          : `${resultUrl}${separator}download=true`
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = this.videoResultFileName(row)
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    videoResultFileName(row) {
      const rawName = String((row && (row.taskNo || row.id)) || 'aigc-video')
          .replace(/[\\/:*?"<>|]+/g, '_')
          .trim() || 'aigc-video'
      return /\.mp4$/i.test(rawName) ? rawName : `${rawName}.mp4`
    },
    closeVideoPreview() {
      this.previewVideoUrl = ''
      this.previewTitle = '视频预览'
    },
    async loadUploadedVideos() {
      this.uploadedVideoLoading = true
      try {
        const response = await listUploadedVideoRecords({
          advertiser_id: this.currentAdvertiserId(),
          upload_person: this.currentUsername(),
          file_name: this.mediaPickerQuery.videoFileName,
          page: 1,
          page_size: 50
        })
        this.uploadedVideos = this.normalizeUploadedVideoRecords(response.data || {})
        this.selectedUploadedVideos = []
      } catch (error) {
        this.uploadedVideos = []
        this.$message.error(error.message || '获取视频库失败')
      } finally {
        this.uploadedVideoLoading = false
      }
    },
    normalizeUploadedVideoRecords(data) {
      if (Array.isArray(data)) {
        return data
      }
      if (Array.isArray(data.records)) {
        return data.records
      }
      if (Array.isArray(data.list)) {
        return data.list
      }
      if (Array.isArray(data.content)) {
        return data.content
      }
      return []
    },
    handleMediaAssetSelectionChange(selection) {
      this.selectedMediaAssets = (selection || []).filter(row => this.canSelectMediaAsset(row))
    },
    handleGeneratedSelectionChange(selection) {
      this.selectedGeneratedTasks = (selection || []).filter(row => this.canSelectGeneratedVideo(row))
    },
    handleUploadedVideoSelectionChange(selection) {
      this.selectedUploadedVideos = (selection || []).filter(row => this.canSelectUploadedVideo(row))
    },
    canSelectMediaAsset(row) {
      return !!(row && row.accessUrl)
    },
    canSelectGeneratedVideo(row) {
      return !!(row && this.generatedVideoUrl(row))
    },
    canSelectUploadedVideo(row) {
      return !!(row && row.previewUrl)
    },
    selectMediaAssets() {
      this.selectMultipleMedia(this.selectedMediaAssets.map(row => ({
        url: row.accessUrl,
        type: row.mediaType,
        fileName: row.originalFileName || row.fileName || '参考素材'
      })))
    },
    selectGeneratedVideos() {
      this.selectMultipleMedia(this.selectedGeneratedTasks.map(row => ({
        url: this.generatedVideoUrl(row),
        type: 'video',
        fileName: (row.taskNo || row.id || 'AI生成结果') + '.mp4'
      })))
    },
    selectUploadedVideos() {
      this.selectMultipleMedia(this.selectedUploadedVideos.map(row => ({
        url: row.previewUrl,
        type: 'video',
        fileName: row.originalFileName || row.fileName || row.videoId || '视频素材'
      })))
    },
    selectMultipleMedia(items) {
      if (!this.mediaPickerTarget) {
        return
      }
      const validItems = (items || []).filter(item => item && item.url)
      if (validItems.length === 0) {
        this.$message.warning('请先勾选可用素材')
        return
      }
      let addedCount = 0
      const appliedItems = []
      validItems.forEach(item => {
        const appliedItem = this.applyMediaToItemOrAppend(this.mediaPickerTarget, item.url, item.type, item.fileName)
        if (appliedItem) {
          addedCount += 1
          appliedItems.push(appliedItem)
        }
      })
      this.finalizePendingMediaMention(appliedItems)
      this.mediaPickerVisible = false
      this.selectedMediaAssets = []
      this.selectedGeneratedTasks = []
      this.selectedUploadedVideos = []
      this.$message.success(`已加入 ${addedCount} 个素材`)
    },
    selectMedia(url, type, fileName) {
      if (!this.mediaPickerTarget || !url) {
        return
      }
      const appliedItem = this.applyMediaToItemOrAppend(this.mediaPickerTarget, url, type, fileName)
      this.finalizePendingMediaMention(appliedItem ? [appliedItem] : [])
      this.mediaPickerVisible = false
    },
    applyMediaToItemOrAppend(targetItem, url, type, fileName) {
      if (!url) {
        return false
      }
      const item = targetItem && !targetItem.url
          ? targetItem
          : this.appendEmptyMedia(type || (targetItem && targetItem.type) || 'image')
      const selectedType = type || item.type || 'image'
      this.$set(item, 'url', this.normalizeSelectedMediaUrl(url))
      if (type) {
        this.$set(item, 'type', type)
      }
      if (fileName) {
        this.$set(item, 'fileName', fileName)
      }
      const role = this.keepMediaRole(selectedType, item.role)
          ? item.role
          : this.defaultMediaRole(selectedType, item)
      this.$set(item, 'role', role)
      return item
    },
    appendEmptyMedia(type) {
      const item = {
        type: type || 'image',
        role: '',
        url: '',
        fileName: '',
        uploading: false
      }
      this.createForm.media.push(item)
      return item
    },
    normalizeSelectedMediaUrl(url) {
      const value = String(url || '').trim()
      if (!value) {
        return ''
      }
      if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith('/') || value.startsWith('data:') || value.startsWith('blob:')) {
        return value
      }
      return this.publicUrl(value)
    },
    keepMediaRole(type, role) {
      if (!role) {
        return false
      }
      if (type === 'video') {
        if (this.createForm.sceneType === 'REFERENCE_TO_VIDEO') {
          return role === 'reference_video'
        }
        return role === 'video'
      }
      if (type === 'image') {
        return ['first_frame', 'last_frame', 'reference_image'].includes(role)
      }
      if (type === 'audio') {
        return this.createForm.sceneType === 'REFERENCE_TO_VIDEO' && role === 'reference_audio'
      }
      return false
    },
    generatedVideoUrl(row) {
      return this.publicUrl(row.resultUrl || row.providerResultUrl || '')
    },
    mediaTypeLabel(value) {
      const map = {
        image: '图片',
        video: '视频',
        audio: '音频'
      }
      return map[value] || value || '-'
    },
    mediaAssetType(row) {
      const declaredType = String((row && (row.mediaType || row.type)) || '').toLowerCase()
      if (['image', 'video', 'audio'].includes(declaredType)) {
        return declaredType
      }
      return this.inferMediaTypeFromUrl((row && row.accessUrl) || '', declaredType)
    },
    roleLabel(value) {
      const map = {
        first_frame: '首帧',
        last_frame: '尾帧',
        reference_image: '参考图',
        reference_video: '参考视频',
        reference_audio: '参考音频',
        video: '视频'
      }
      return map[value] || '-'
    },
    mediaAccept(type) {
      const map = {
        image: 'image/*',
        video: 'video/*',
        audio: 'audio/*'
      }
      return map[type] || 'image/*,video/*,audio/*'
    },
    beforeMediaUpload(file) {
      const type = this.detectUploadMediaType(file)
      if (!type) {
        this.$message.warning('请选择图片、视频或音频文件')
        return false
      }
      file.aigcMediaType = type
      return true
    },
    detectUploadMediaType(file) {
      const contentType = file.type || ''
      const name = (file.name || '').toLowerCase()
      if (contentType.indexOf('image/') === 0 || /\.(jpg|jpeg|png|webp|gif)$/.test(name)) {
        return 'image'
      }
      if (contentType.indexOf('video/') === 0 || /\.(mp4|mov|m4v|webm|avi|mkv)$/.test(name)) {
        return 'video'
      }
      if (contentType.indexOf('audio/') === 0 || /\.(mp3|wav|m4a|aac|ogg|flac)$/.test(name)) {
        return 'audio'
      }
      return ''
    },
    async uploadMedia(request, item) {
      const file = request.file
      const mediaType = file.aigcMediaType || this.detectUploadMediaType(file) || item.type || 'image'
      const uploadingCount = Number(item.uploadingCount || 0) + 1
      this.$set(item, 'uploadingCount', uploadingCount)
      this.$set(item, 'uploading', true)
      try {
        const response = await uploadAigcReferenceMedia(file, {
          mediaType,
          uploaderUserId: this.currentUserId(),
          uploadedBy: this.currentUsername()
        })
        const media = response.data || {}
        this.applyMediaToItemOrAppend(item, media.accessUrl || '', media.mediaType || mediaType, media.originalFileName || file.name)
        if (request.onSuccess) {
          request.onSuccess(media)
        }
        this.$message.success('素材已上传')
      } catch (error) {
        if (request.onError) {
          request.onError(error)
        }
        this.$message.error(error.message || '素材上传失败')
      } finally {
        const nextCount = Math.max(0, Number(item.uploadingCount || 1) - 1)
        this.$set(item, 'uploadingCount', nextCount)
        this.$set(item, 'uploading', nextCount > 0)
      }
    },
    defaultMediaRole(type, currentItem) {
      if (type === 'video') {
        return this.createForm.sceneType === 'REFERENCE_TO_VIDEO' ? 'reference_video' : 'video'
      }
      if (type === 'audio') {
        return this.createForm.sceneType === 'REFERENCE_TO_VIDEO' ? 'reference_audio' : ''
      }
      if (type !== 'image') {
        return ''
      }
      if (this.createForm.sceneType === 'IMAGE_PAIR_TO_VIDEO') {
        const hasFirstFrame = this.createForm.media.some(item => item !== currentItem && item.role === 'first_frame')
        const hasLastFrame = this.createForm.media.some(item => item !== currentItem && item.role === 'last_frame')
        if (!hasFirstFrame) return 'first_frame'
        if (!hasLastFrame) return 'last_frame'
      }
      return this.createForm.sceneType === 'IMAGE_TO_VIDEO' ? 'first_frame' : 'reference_image'
    },
    creativePromptTextarea() {
      const input = this.$refs.creativePromptEditor
      return input && input.$refs ? input.$refs.textarea : null
    },
    handlePromptInput() {
      this.$nextTick(() => {
        const textarea = this.creativePromptTextarea()
        if (!textarea) {
          return
        }
        const cursor = typeof textarea.selectionStart === 'number'
            ? textarea.selectionStart
            : String(this.createForm.prompt || '').length
        const beforeCursor = String(this.createForm.prompt || '').slice(0, cursor)
        const match = beforeCursor.match(/@([^\s@]{0,30})$/)
        if (!match) {
          this.closePromptMentionMenu()
          return
        }
        this.promptMentionStart = cursor - match[0].length
        this.promptMentionEnd = cursor
        this.promptMentionQuery = match[1] || ''
        this.promptMentionActiveIndex = 0
        this.promptMentionMenuOpen = true
      })
    },
    openPromptMentionMenu() {
      if (this.promptMentionCloseTimer) {
        window.clearTimeout(this.promptMentionCloseTimer)
        this.promptMentionCloseTimer = null
      }
      const textarea = this.creativePromptTextarea()
      const cursor = textarea && typeof textarea.selectionStart === 'number'
          ? textarea.selectionStart
          : String(this.createForm.prompt || '').length
      this.promptMentionStart = cursor
      this.promptMentionEnd = cursor
      this.promptMentionQuery = ''
      this.promptMentionActiveIndex = 0
      this.promptMentionMenuOpen = true
      this.$nextTick(() => {
        if (textarea) textarea.focus()
      })
    },
    scheduleClosePromptMentionMenu() {
      if (this.promptMentionCloseTimer) {
        window.clearTimeout(this.promptMentionCloseTimer)
      }
      this.promptMentionCloseTimer = window.setTimeout(() => {
        this.promptMentionCloseTimer = null
        if (!this.mediaPickerVisible) {
          this.closePromptMentionMenu()
        }
      }, 120)
    },
    closePromptMentionMenu() {
      this.promptMentionMenuOpen = false
      this.promptMentionQuery = ''
      this.promptMentionActiveIndex = 0
    },
    selectPromptMentionEntry(entry) {
      if (!entry) {
        return
      }
      if (entry.kind === 'media') {
        const item = this.createForm.media[entry.item.index]
        this.insertPromptMediaMentions(item ? [item] : [])
        return
      }
      if (entry.kind === 'upload') {
        this.openPromptMentionLocalUpload()
        return
      }
      this.openMentionMediaPicker(entry.mediaType)
    },
    openPromptMentionLocalUpload() {
      if (this.promptMentionUploading) {
        this.$message.info('素材正在上传，请稍候')
        return
      }
      this.pendingMediaMention = {
        start: this.promptMentionStart,
        end: this.promptMentionEnd
      }
      this.closePromptMentionMenu()
      const input = this.$refs.promptMentionUploadInput
      if (!input) {
        this.pendingMediaMention = null
        this.$message.error('本地上传组件未准备好，请刷新页面后重试')
        return
      }
      input.value = ''
      this.promptMentionFileDialogActive = true
      window.addEventListener('focus', () => {
        window.setTimeout(() => {
          if (this.promptMentionFileDialogActive && (!input.files || input.files.length === 0)) {
            this.promptMentionFileDialogActive = false
            this.removePendingMentionTrigger(this.pendingMediaMention)
            this.pendingMediaMention = null
          }
        }, 500)
      }, { once: true })
      input.click()
    },
    async handlePromptMentionLocalFiles(event) {
      const input = event && event.target
      const files = Array.from((input && input.files) || [])
      this.promptMentionFileDialogActive = false
      if (files.length === 0) {
        return
      }
      const validFiles = files.filter(file => this.beforeMediaUpload(file) !== false)
      if (validFiles.length === 0) {
        this.removePendingMentionTrigger(this.pendingMediaMention)
        this.pendingMediaMention = null
        if (input) input.value = ''
        return
      }
      const mediaTypes = validFiles.map(file => file.aigcMediaType || this.detectUploadMediaType(file))
      if (mediaTypes.some(type => type === 'video' || type === 'audio')) {
        this.ensureSceneForMentionType('video')
      } else {
        this.ensureSceneForMentionType('image')
      }
      this.promptMentionUploading = true
      const uploadItems = validFiles.map(file => ({
        file,
        item: this.appendEmptyMedia(file.aigcMediaType || this.detectUploadMediaType(file) || 'image')
      }))
      try {
        await Promise.all(uploadItems.map(({ file, item }) => this.uploadMedia({ file }, item)))
        const uploadedItems = uploadItems.map(entry => entry.item).filter(item => item && item.url)
        uploadItems.forEach(({ item }) => {
          if (!item.url) {
            const index = this.createForm.media.indexOf(item)
            if (index >= 0) this.createForm.media.splice(index, 1)
          }
        })
        if (uploadedItems.length > 0) {
          this.finalizePendingMediaMention(uploadedItems)
          this.$message.success(`已引用 ${uploadedItems.length} 个本地素材`)
        } else {
          this.removePendingMentionTrigger(this.pendingMediaMention)
          this.pendingMediaMention = null
        }
      } finally {
        this.promptMentionUploading = false
        if (input) input.value = ''
      }
    },
    openMentionMediaPicker(type) {
      this.pendingMediaMention = {
        start: this.promptMentionStart,
        end: this.promptMentionEnd
      }
      this.closePromptMentionMenu()
      this.ensureSceneForMentionType(type)
      const item = this.appendEmptyMedia(type || 'image')
      this.pendingMediaMention.target = item
      this.openMediaPicker(item)
    },
    ensureSceneForMentionType(type) {
      if (type === 'image' && this.createForm.sceneType === 'TEXT_TO_VIDEO') {
        this.setCreativeScene('IMAGE_TO_VIDEO')
      } else if (['video', 'audio'].includes(type)
          && !['REFERENCE_TO_VIDEO', 'VIDEO_EDIT', 'VIDEO_EXTEND'].includes(this.createForm.sceneType)) {
        this.setCreativeScene('REFERENCE_TO_VIDEO')
      }
    },
    finalizePendingMediaMention(items) {
      if (!this.pendingMediaMention || !items || items.length === 0) {
        return
      }
      const range = this.pendingMediaMention
      this.pendingMediaMention = null
      this.insertPromptMediaMentions(items, range)
    },
    insertPromptMediaMentions(items, range) {
      const validItems = (items || []).filter(item => item && item.url)
      if (validItems.length === 0) {
        return
      }
      const labels = validItems.map(item => `@${this.ensureMediaMentionLabel(item)}`)
      const prompt = String(this.createForm.prompt || '')
      const start = Math.max(0, Math.min(range ? range.start : this.promptMentionStart, prompt.length))
      const end = Math.max(start, Math.min(range ? range.end : this.promptMentionEnd, prompt.length))
      const insertedText = `${labels.join(' ')} `
      this.createForm.prompt = prompt.slice(0, start) + insertedText + prompt.slice(end)
      this.closePromptMentionMenu()
      this.$nextTick(() => {
        const textarea = this.creativePromptTextarea()
        if (textarea) {
          const cursor = start + insertedText.length
          textarea.focus()
          textarea.setSelectionRange(cursor, cursor)
        }
      })
    },
    insertMediaMentionFromCard(index) {
      const item = this.createForm.media[index]
      if (!item || !item.url) {
        return
      }
      const textarea = this.creativePromptTextarea()
      const cursor = textarea && typeof textarea.selectionStart === 'number'
          ? textarea.selectionStart
          : String(this.createForm.prompt || '').length
      this.insertPromptMediaMentions([item], { start: cursor, end: cursor })
    },
    ensureMediaMentionLabel(item) {
      if (item._mentionLabel) {
        return item._mentionLabel
      }
      const prefix = item.role === 'first_frame'
          ? '首帧图'
          : item.role === 'last_frame'
              ? '尾帧图'
              : {
                image: '参考图',
                video: '参考视频',
                audio: '参考音频'
              }[item.type] || '素材'
      const label = `${prefix}${this.nextMediaMentionId}`
      this.nextMediaMentionId += 1
      this.$set(item, '_mentionLabel', label)
      return label
    },
    removePromptMediaMention(item) {
      if (!item || !item._mentionLabel) {
        return
      }
      const token = `@${item._mentionLabel}`
      this.createForm.prompt = String(this.createForm.prompt || '').split(token).join('')
    },
    removePendingMentionTrigger(range) {
      if (!range) {
        return
      }
      const prompt = String(this.createForm.prompt || '')
      const start = Math.max(0, Math.min(range.start, prompt.length))
      const end = Math.max(start, Math.min(range.end, prompt.length))
      if (prompt.slice(start, end).startsWith('@')) {
        this.createForm.prompt = prompt.slice(0, start) + prompt.slice(end)
      }
    },
    mediaMentionIcon(type) {
      const map = {
        image: 'el-icon-picture-outline',
        video: 'el-icon-video-camera',
        audio: 'el-icon-headset'
      }
      return map[type] || 'el-icon-paperclip'
    },
    resolvePromptMediaMentions(prompt) {
      let resolvedPrompt = String(prompt || '')
      const counters = { image: 0, video: 0, audio: 0 }
      const mediaItems = (this.createForm.media || []).filter(item => item && item.url)
      mediaItems.forEach(item => {
        const type = ['image', 'video', 'audio'].includes(item.type) ? item.type : 'image'
        counters[type] += 1
        if (!item._mentionLabel) {
          return
        }
        const replacement = item.role === 'first_frame'
            ? '首帧图片'
            : item.role === 'last_frame'
                ? '尾帧图片'
                : type === 'image'
                    ? `第${counters[type]}张参考图`
            : type === 'video'
                ? `第${counters[type]}个参考视频`
                : `第${counters[type]}段参考音频`
        resolvedPrompt = resolvedPrompt.split(`@${item._mentionLabel}`).join(replacement)
      })
      return resolvedPrompt
    },
    handlePromptKeydown(event) {
      if (!event || event.isComposing || event.key !== 'Enter') {
        if (event && this.promptMentionMenuOpen) {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            const direction = event.key === 'ArrowDown' ? 1 : -1
            const total = this.promptMentionEntries.length
            if (total > 0) {
              this.promptMentionActiveIndex = (this.promptMentionActiveIndex + direction + total) % total
            }
          } else if (event.key === 'Escape') {
            event.preventDefault()
            this.closePromptMentionMenu()
          }
        }
        return
      }
      if (this.promptMentionMenuOpen) {
        event.preventDefault()
        const entry = this.promptMentionEntries[this.promptMentionActiveIndex]
        this.selectPromptMentionEntry(entry)
        return
      }
      if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
        event.preventDefault()
        this.insertPromptLineBreak(event)
      }
    },
    insertPromptLineBreak(event) {
      const target = event.target
      const value = this.createForm.prompt || ''
      const start = target && typeof target.selectionStart === 'number' ? target.selectionStart : value.length
      const end = target && typeof target.selectionEnd === 'number' ? target.selectionEnd : start
      this.createForm.prompt = value.slice(0, start) + '\n' + value.slice(end)
      this.$nextTick(() => {
        if (target && target.setSelectionRange) {
          target.focus()
          target.setSelectionRange(start + 1, start + 1)
        }
      })
    },
    async openPromptHistoryDialog() {
      this.promptHistoryVisible = true
      await this.loadPromptHistory()
    },
    async loadPromptHistory() {
      this.promptHistoryLoading = true
      try {
        const params = {
          page: 0,
          size: 50
        }
        const username = this.currentUsername()
        if (username && username !== 'unknown') {
          params.createdBy = username
        }
        const response = await listAigcVideoTasks(params)
        const pageData = response.data || {}
        const seenPrompts = new Set()
        this.promptHistoryTasks = (pageData.content || [])
            .filter(item => item.prompt)
            .filter(item => {
              const prompt = String(item.prompt || '').trim()
              if (!prompt || seenPrompts.has(prompt)) {
                return false
              }
              seenPrompts.add(prompt)
              return true
            })
      } catch (error) {
        this.promptHistoryTasks = []
        this.$message.error(error.message || '获取历史提示词失败')
      } finally {
        this.promptHistoryLoading = false
      }
    },
    continueWithPrompt(row) {
      const prompt = String((row && row.prompt) || '').trim()
      if (!prompt) {
        this.$message.warning('这条任务没有可使用的提示词')
        return
      }
      this.createForm.provider = row.provider || this.createForm.provider
      this.createForm.sceneType = row.sceneType || this.createForm.sceneType
      this.createForm.model = row.model || this.createForm.model
      if (this.createForm.provider === 'SEEDANCE' && !this.currentModelOptions.some(item => item.value === this.createForm.model)) {
        this.applyRecommendedModel(true)
      }
      this.copyTaskOptionsToCreateForm(row)
      this.syncProviderKeySelection()
      if (!this.createForm.model) {
        this.applyRecommendedModel(false)
      }
      this.applyModelConstraints()
      this.createForm.prompt = prompt
      this.continuedFromTask = {
        id: row.id,
        taskNo: row.taskNo
      }
      this.promptHistoryVisible = false
      this.focusPromptEditor()
      this.$message.success('已带入历史提示词，可继续编辑')
    },
    copyTaskOptionsToCreateForm(row) {
      if (row.resolution) this.createForm.resolution = row.resolution
      if (row.ratio) this.createForm.ratio = row.ratio
      if (row.duration !== undefined && row.duration !== null) this.createForm.duration = Number(row.duration)
      if (row.frames !== undefined && row.frames !== null) this.createForm.frames = Number(row.frames)
      if (row.seed !== undefined && row.seed !== null) this.createForm.seed = Number(row.seed)
      if (row.providerKeyId !== undefined && row.providerKeyId !== null) this.createForm.providerKeyId = this.keyOptionValue(row.providerKeyId)
      if (typeof row.generateAudio === 'boolean') this.createForm.generateAudio = row.generateAudio
    },
    focusPromptEditor() {
      this.$nextTick(() => {
        const panel = this.activeMainTab === 'creative'
            ? this.$el.querySelector('.creative-workspace')
            : this.$el.querySelector('.create-panel')
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        const textarea = this.activeMainTab === 'creative'
            ? this.$el.querySelector('.creative-prompt-editor textarea')
            : this.$el.querySelector('.create-panel textarea')
        if (textarea) {
          textarea.focus()
          const cursor = (this.createForm.prompt || '').length
          if (textarea.setSelectionRange) {
            textarea.setSelectionRange(cursor, cursor)
          }
        }
      })
    },
    currentCreateFormRef() {
      const refName = this.activeMainTab === 'creative' ? 'creativeCreateForm' : 'createForm'
      const formRef = this.$refs[refName] || this.$refs.createForm || this.$refs.creativeCreateForm
      return Array.isArray(formRef) ? formRef[0] : formRef
    },
    async refreshCreateOptionsBeforeSubmit() {
      const previousKey = this.keyOptionValue(this.createForm.providerKeyId)
      const previousModel = this.createForm.model
      await this.loadVideoProviderConfigs()
      await this.loadVideoModelConfigs()
      this.syncProviderKeySelection()
      if (previousModel && this.currentModelOptions.some(item => item.value === previousModel)) {
        this.createForm.model = previousModel
      } else {
        this.applyRecommendedModel(true)
      }
      this.applyModelConstraints()
      if (previousKey && previousKey !== this.keyOptionValue(this.createForm.providerKeyId)) {
        this.$message.warning('当前 Key 已关闭或不可用，已切换到可用 Key')
      }
    },
    async createTask() {
      await this.refreshCreateOptionsBeforeSubmit()
      if (!this.videoProviderOptions.length) {
        this.$message.warning('暂无可用视频生成服务商，请先在服务商配置中启用')
        return
      }
      if (!this.videoProviderOptions.some(item => item.value === this.createForm.provider)) {
        this.$message.warning('当前服务商已关闭，请重新选择')
        this.syncVideoProviderSelection()
        return
      }
      this.syncProviderKeySelection()
      if (this.showProviderKeySelect && !this.currentProviderKeyOptions.length) {
        this.$message.warning('暂无可用 Seedance Key，请先在服务商配置中添加或启用 Key')
        return
      }
      this.createForm.generateAudio = true
      this.applyModelConstraints()
      this.normalizeCreateMediaRoles()
      const formRef = this.currentCreateFormRef()
      if (!formRef || !formRef.validate) {
        this.$message.error('生成表单未准备好，请刷新页面后重试')
        return
      }
      formRef.validate(async valid => {
        if (!valid) return
        if (!this.normalizedPrompt() && !this.createForm.media.some(item => item.url)) {
          this.$message.warning('请填写提示词或素材 URL')
          return
        }
        this.creating = true
        try {
          const response = await createAigcVideoTask(this.buildCreatePayload())
          const task = response.data || {}
          this.prependCreatedTask(task)
          this.createForm.prompt = ''
          this.createForm.media = []
          this.nextMediaMentionId = 1
          this.closePromptMentionMenu()
          this.continuedFromTask = null
          this.pagination.page = 1
          if (task.status === 'FAILED' && task.errorMessage) {
            this.$message.warning('任务提交失败：' + this.friendlyTaskError(task.errorMessage))
          } else {
            this.$message.success('任务已提交')
          }
          this.loadTasks({ silent: true, background: true }).catch(() => {})
        } catch (error) {
          this.$message.error(error.message || '提交任务失败')
        } finally {
          this.creating = false
        }
      })
    },
    prependCreatedTask(task) {
      if (!task || (!task.id && !task.taskNo)) {
        return
      }
      const taskKey = task.id || task.taskNo
      const existingTasks = (this.tasks || []).filter(item => (item.id || item.taskNo) !== taskKey)
      this.tasks = [task].concat(existingTasks).slice(0, this.pagination.pageSize)
      this.pagination.total = Math.max(Number(this.pagination.total || 0) + 1, this.tasks.length)
    },
    buildCreatePayload() {
      const options = {}
      this.putOption(options, 'resolution', this.createForm.resolution)
      this.putOption(options, 'ratio', this.createForm.ratio)
      this.putOption(options, 'duration', this.createForm.duration)
      if (!this.framesDisabled) {
        this.putOption(options, 'frames', this.createForm.frames)
      }
      this.putOption(options, 'seed', this.createForm.seed)
      this.putOption(options, 'generate_audio', this.createForm.generateAudio)
      return {
        creatorUserId: this.currentUserId(),
        createdBy: this.currentUsername(),
        provider: this.createForm.provider,
        providerKeyId: this.normalizedProviderKeyId(),
        providerKeyLegacy: this.selectedProviderKeyLegacy(),
        sceneType: this.createForm.sceneType,
        model: this.createForm.model,
        prompt: this.normalizedPrompt(),
        media: this.createForm.media
            .filter(item => item.url)
            .map(item => {
              const type = item.type || 'image'
              const role = this.keepMediaRole(type, item.role)
                  ? item.role
                  : this.defaultMediaRole(type, item)
              return {
                type,
                role,
                url: item.url
              }
            }),
        options
      }
    },
    buildBillingEstimatePayload() {
      const payload = this.buildCreatePayload()
      return {
        provider: payload.provider,
        providerKeyId: payload.providerKeyId,
        providerKeyLegacy: payload.providerKeyLegacy,
        sceneType: payload.sceneType,
        model: payload.model,
        media: (payload.media || []).map(item => ({ type: item.type, role: item.role })),
        options: payload.options
      }
    },
    normalizedPrompt() {
      return this.resolvePromptMediaMentions(this.createForm.prompt).trim()
    },
    putOption(target, key, value) {
      if (value !== undefined && value !== null && value !== '') {
        target[key] = value
      }
    },
    async loadTasks(options = {}) {
      const silent = !!options.silent
      const background = !!options.background
      if (!background) {
        this.taskLoading = true
      }
      try {
        const dateRange = this.query.dateRange || []
        const params = {
          provider: this.query.provider,
          status: this.query.status,
          startDate: dateRange[0],
          endDate: dateRange[1],
          favoriteOnly: this.query.favoriteOnly,
          favoriteUserId: this.currentUserId(),
          favoriteBy: this.currentUsername(),
          page: this.pagination.page - 1,
          size: this.pagination.pageSize
        }
        const response = await listAigcVideoTasks(params)
        const pageData = response.data || {}
        if (background && this.selectedSyncTasks.length > 0) {
          return
        }
        this.tasks = pageData.content || []
        this.selectedSyncTasks = []
        this.pagination.total = Number(pageData.totalElements || 0)
      } catch (error) {
        if (!silent) {
          this.$message.error(error.message || '获取任务列表失败')
        }
      } finally {
        if (!background) {
          this.taskLoading = false
        }
      }
    },
    async loadBillingSummary(options = {}) {
      const silent = !!options.silent
      this.billingLoading = true
      try {
        const dateRange = this.query.dateRange || []
        const response = await getAigcVideoBillingSummary({
          provider: this.query.provider,
          status: this.query.status,
          startDate: dateRange[0],
          endDate: dateRange[1],
          favoriteOnly: this.query.favoriteOnly,
          favoriteUserId: this.currentUserId(),
          favoriteBy: this.currentUsername(),
          periodType: this.billingPeriod
        })
        const data = response.data || {}
        this.billingSummary = Object.assign({
          totalTaskCount: 0,
          chargedTaskCount: 0,
          totalUsageTokens: 0,
          totalGeneratedDurationSeconds: 0,
          totalEstimatedCost: 0,
          rows: [],
          userRows: [],
          modelRows: []
        }, data)
      } catch (error) {
        if (!silent) {
          this.$message.error(error.message || '获取费用统计失败')
        }
        this.billingSummary = {
          totalTaskCount: 0,
          chargedTaskCount: 0,
          totalUsageTokens: 0,
          totalGeneratedDurationSeconds: 0,
          totalEstimatedCost: 0,
          rows: [],
          userRows: [],
          modelRows: []
        }
      } finally {
        this.billingLoading = false
      }
    },
    searchTasks() {
      this.pagination.page = 1
      this.loadTasks()
      this.loadBillingSummary()
    },
    refreshData() {
      this.loadTasks()
      this.loadBillingSummary()
    },
    handleBillingPeriodChange() {
      this.loadBillingSummary()
    },
    handleBillingGroupChange() {
      try {
        localStorage.setItem('aigcVideoBillingGroup', this.billingGroup)
      } catch (e) {
        // 本地状态保存失败不影响页面使用。
      }
    },
    toggleBillingCollapsed() {
      this.billingCollapsed = !this.billingCollapsed
      try {
        localStorage.setItem('aigcVideoBillingCollapsed', String(this.billingCollapsed))
      } catch (e) {
        // 本地状态保存失败不影响页面使用。
      }
      if (!this.billingCollapsed && !(this.billingSummary.rows || []).length) {
        this.loadBillingSummary({ silent: true })
      }
    },
    async toggleFavorite(row) {
      if (!row || !row.id) {
        return
      }
      const nextFavorite = !row.favorite
      try {
        const response = await setAigcVideoTaskFavorite(row.id, {
          favorite: nextFavorite,
          userId: this.currentUserId(),
          username: this.currentUsername()
        })
        const data = response.data || {}
        this.$set(row, 'favorite', !!data.favorite)
        this.$set(row, 'favoritedAt', data.favoritedAt || '')
        this.$message.success(nextFavorite ? '已收藏' : '已取消收藏')
        if (!nextFavorite && this.query.favoriteOnly) {
          this.loadTasks()
          this.loadBillingSummary({ silent: true })
        }
      } catch (error) {
        this.$message.error(error.message || '收藏操作失败')
      }
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadTasks()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadTasks()
    },
    canSelectTaskForSync(row) {
      return row && this.isOwnTask(row) && !this.isTerminal(row.status)
    },
    handleTaskSelectionChange(selection) {
      this.selectedSyncTasks = (selection || []).filter(row => this.canSelectTaskForSync(row))
    },
    async syncSelectedTasks() {
      const rows = this.selectedSyncTasks.filter(row => row && row.id && !this.isTerminal(row.status))
      if (rows.length === 0) {
        this.$message.warning('请先勾选可同步的任务')
        return
      }
      this.batchSyncing = true
      let successCount = 0
      let failedCount = 0
      for (const row of rows) {
        try {
          await syncAigcVideoTask(row.id, this.currentUsername())
          successCount += 1
        } catch (error) {
          failedCount += 1
        }
      }
      await this.loadTasks()
      await this.loadBillingSummary({ silent: true })
      this.batchSyncing = false
      if (failedCount > 0) {
        this.$message.warning(`同步完成：成功 ${successCount} 个，失败 ${failedCount} 个`)
      } else {
        this.$message.success(`已同步 ${successCount} 个任务`)
      }
    },
    async syncTask(row) {
      try {
        await syncAigcVideoTask(row.id, this.currentUsername())
        this.$message.success('任务状态已同步')
        await this.loadTasks()
        await this.loadBillingSummary({ silent: true })
      } catch (error) {
        this.$message.error(error.message || '同步任务失败')
      }
    },
    async cancelTask(row) {
      try {
        await this.$confirm('确认取消该生成任务？', '取消确认', {
          confirmButtonText: '确认取消',
          cancelButtonText: '返回',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      try {
        await cancelAigcVideoTask(row.id, this.currentUsername())
        this.$message.success('任务已取消')
        await this.loadTasks()
        await this.loadBillingSummary({ silent: true })
      } catch (error) {
        this.$message.error(error.message || '取消任务失败')
      }
    },
    async openEventDialog(row) {
      this.currentEventTask = row
      this.eventDialogVisible = true
      this.eventLoading = true
      try {
        const response = await listAigcVideoTaskEvents(row.id)
        this.eventLogs = response.data || []
      } catch (error) {
        this.eventLogs = []
        this.$message.error(error.message || '获取任务日志失败')
      } finally {
        this.eventLoading = false
      }
    },
    showEventPayload(row) {
      const text = this.prettyJson(row.eventPayload)
      this.$alert(`<pre class="event-payload-pre">${this.escapeHtml(text)}</pre>`, '事件详情', {
        dangerouslyUseHTMLString: true,
        customClass: 'event-payload-dialog',
        confirmButtonText: '关闭'
      })
    },
    prettyJson(value) {
      if (!value) return ''
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch (e) {
        return String(value)
      }
    },
    escapeHtml(value) {
      return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;')
    },
    currentUsername() {
      if (this.$store.state.username) return this.$store.state.username
      const storeUser = this.$store.state.user || {}
      if (storeUser.username) return storeUser.username
      try {
        const localUser = JSON.parse(localStorage.getItem('user') || '{}')
        return localUser.username || 'unknown'
      } catch (e) {
        return 'unknown'
      }
    },
    currentUserId() {
      const storeUser = this.$store.state.user || {}
      if (storeUser.id || storeUser.userId) return String(storeUser.id || storeUser.userId)
      try {
        const localUser = JSON.parse(localStorage.getItem('user') || '{}')
        return localUser.id || localUser.userId ? String(localUser.id || localUser.userId) : ''
      } catch (e) {
        return ''
      }
    },
    currentAccountType() {
      const storeUser = this.$store.state.user || {}
      const localUser = this.localStoredUser()
      const value = this.firstDefined(
          storeUser.accountType,
          storeUser.account_type,
          storeUser.userType,
          storeUser.user_type,
          storeUser.type,
          localUser.accountType,
          localUser.account_type,
          localUser.userType,
          localUser.user_type,
          localUser.type
      )
      return value !== undefined && value !== null ? String(value) : ''
    },
    currentUserCanViewAllTasks() {
      const storeUser = this.$store.state.user || {}
      const localUser = this.localStoredUser()
      const accountType = this.currentAccountType()
      const role = String(this.firstDefined(storeUser.role, storeUser.userRole, localUser.role, localUser.userRole, '')).toLowerCase()
      const username = String(this.currentUsername() || '').toLowerCase()
      return accountType === '1'
          || this.truthy(storeUser.admin)
          || this.truthy(storeUser.isAdmin)
          || this.truthy(localUser.admin)
          || this.truthy(localUser.isAdmin)
          || role === 'admin'
          || role === 'administrator'
          || username === 'admin'
          || username === 'administrator'
    },
    localStoredUser() {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}') || {}
      } catch (e) {
        return {}
      }
    },
    firstDefined() {
      for (let i = 0; i < arguments.length; i += 1) {
        const value = arguments[i]
        if (value !== undefined && value !== null && value !== '') {
          return value
        }
      }
      return undefined
    },
    truthy(value) {
      return value === true || value === 1 || String(value).toLowerCase() === 'true'
    },
    currentAdvertiserId() {
      return localStorage.getItem('advertiser_id') || '7598883753192357896'
    },
    providerLabel(value) {
      const map = { SEEDANCE: 'Seedance', DASHSCOPE: 'DashScope' }
      return map[value] || value || '-'
    },
    friendlyTaskError(value) {
      const text = String(value || '').trim()
      if (!text) {
        return '-'
      }
      let message = text
      try {
        const parsed = JSON.parse(text)
        message = (parsed.error && parsed.error.message) || parsed.message || text
      } catch (e) {
        message = text
      }
      if (!message) {
        return text
      }
      if (message.indexOf('参考图尺寸过小') >= 0) {
        return message
      }
      if (message.indexOf('参考视频可能包含真人') >= 0) {
        return message
      }
      if (/requires audio role to be reference_audio/i.test(message)) {
        return '参考音频参数不符合 Seedance 要求：参考媒体模式下音频素材角色必须为 reference_audio。请重新提交任务。'
      }
      const realPersonMatch = message.match(/input video [`']?content\[(\d+)]/i)
      if (realPersonMatch && /may contain real person/i.test(message)) {
        const contentIndex = Number(realPersonMatch[1])
        const mediaHint = contentIndex > 0 ? `有提示词时通常对应第 ${contentIndex} 个媒体素材。` : ''
        return `参考视频可能包含真人，Seedance 已拒绝处理。${mediaHint}请换成不包含真人的游戏录屏、动画或卡通素材后重新提交。`
      }
      const dimensionMatch = message.match(/expected the (width|height) to be at least (\d+)px, but received a (\d+)x(\d+)px image/i)
      if (dimensionMatch) {
        const sideLabel = dimensionMatch[1].toLowerCase() === 'width' ? '宽度' : '高度'
        return `参考图尺寸过小：当前 ${dimensionMatch[3]}x${dimensionMatch[4]}，Seedance 要求${sideLabel}至少 ${dimensionMatch[2]}px。请重新提交让系统自动放大，或换一张更大的参考图。`
      }
      return message
    },
    sceneLabel(value) {
      const match = this.sceneOptions.find(item => item.value === value)
      return match ? match.label : (value || '-')
    },
    modelLabel(value, provider) {
      const model = String(value || '').trim()
      if (!model) {
        return '-'
      }
      const match = this.modelOptionList(provider).find(item => item.value === model)
      return match && match.label ? match.label : model
    },
    modelDisplayName(value, provider) {
      const model = String(value || '').trim()
      if (!model) {
        return '-'
      }
      const label = this.modelLabel(model, provider)
      const suffix = ' - ' + model
      return label.endsWith(suffix) ? label.slice(0, -suffix.length) : label
    },
    modelOptionList(provider) {
      const options = this.modelOptions[provider]
      if (Array.isArray(options)) {
        return options
      }
      if (!options || typeof options !== 'object') {
        return []
      }
      return Object.keys(options).reduce((list, key) => list.concat(options[key] || []), [])
    },
    statusLabel(value) {
      const match = this.statusOptions.find(item => item.value === value)
      return match ? match.label : (value || '-')
    },
    statusType(value) {
      const map = {
        CREATED: 'info',
        SUBMITTED: 'info',
        QUEUED: 'warning',
        RUNNING: 'warning',
        SUCCEEDED: 'success',
        FAILED: 'danger',
        CANCELLED: 'info',
        EXPIRED: 'danger'
      }
      return map[value] || 'info'
    },
    eventTypeLabel(value) {
      const map = {
        CREATED: '创建',
        MEDIA_NORMALIZED: '素材尺寸处理',
        SUBMITTED: '已提交',
        SUBMIT_FAILED: '提交失败',
        SUBMIT_EXCEPTION: '提交异常',
        MANUAL_SYNC: '手动同步',
        POLL_SYNC: '自动轮询',
        WEBHOOK_SYNC: '回调同步',
        RESULT_SAVED: '结果保存',
        RESULT_SAVE_FAILED: '保存失败',
        FAVORITED: '收藏',
        UNFAVORITED: '取消收藏',
        CANCELLED: '取消'
      }
      return map[value] || value || '-'
    },
    isTerminal(status) {
      return ['SUCCEEDED', 'FAILED', 'CANCELLED', 'EXPIRED'].includes(status)
    },
    isOwnTask(row) {
      return !!(row && row.ownedByCurrentUser !== false && !row.sharedWithCurrentUser)
    },
    formatTime(value) {
      if (!value) return '-'
      const text = String(value)
      if (/^\d+$/.test(text)) {
        const timestamp = Number(text)
        if (timestamp > 0) {
          const date = new Date(timestamp)
          if (!Number.isNaN(date.getTime())) {
            const pad = number => String(number).padStart(2, '0')
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
          }
        }
      }
      return text.replace('T', ' ').slice(0, 19)
    },
    formatInteger(value) {
      const number = Number(value || 0)
      if (!Number.isFinite(number)) {
        return '-'
      }
      return number.toLocaleString()
    },
    formatCurrency(value) {
      if (value === undefined || value === null || value === '') {
        return '-'
      }
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return '-'
      }
      return '¥' + number.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    billingUnitPriceText(row) {
      const unitPrice = row && row.unitPricePerMillionTokens
      if (unitPrice === undefined || unitPrice === null || unitPrice === '') {
        return '-'
      }
      const basePrice = row.baseUnitPricePerMillionTokens
      const discountFactor = Number(row.discountFactor || row.billingDiscountFactor || 0)
      if (basePrice !== undefined && basePrice !== null && basePrice !== '' && discountFactor > 0 && discountFactor < 1) {
        return `${unitPrice} 元/百万，原价 ${basePrice}，${this.discountText(discountFactor)}`
      }
      return `${unitPrice} 元/百万`
    },
    discountText(value) {
      const factor = Number(value)
      if (!Number.isFinite(factor) || factor <= 0 || factor >= 1) {
        return ''
      }
      const discount = Number((factor * 10).toFixed(2))
      return `${discount}折`
    },
    formatDuration(value) {
      const totalSeconds = Math.floor(Number(value || 0))
      if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
        return '0秒'
      }
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      if (hours > 0) {
        return `${hours}小时${minutes}分`
      }
      if (minutes > 0) {
        return `${minutes}分${seconds}秒`
      }
      return `${seconds}秒`
    }
  }
}
</script>

<style scoped>
.aigc-video-page {
  min-height: 100%;
  padding: 18px;
  background: #f5f7fb;
  color: #303133;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.page-head h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: 0;
}

.page-head p {
  margin: 4px 0 0;
  color: #606266;
  font-size: 13px;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.aigc-main-tabs {
  margin-top: 4px;
}

::v-deep .aigc-main-tabs > .el-tabs__header {
  margin: 0 0 14px;
}

::v-deep .aigc-main-tabs > .el-tabs__header .el-tabs__nav-wrap::after {
  height: 1px;
  background: #e5e7eb;
}

.creative-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  gap: 16px;
  align-items: start;
}

.creative-main,
.creative-side > div {
  min-width: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.creative-main {
  padding: 20px;
}

.creative-greeting {
  margin-bottom: 16px;
}

.creative-kicker {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  font-size: 12px;
  line-height: 22px;
}

.creative-greeting h3 {
  margin: 12px 0 6px;
  color: #111827;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  letter-spacing: 0;
}

.creative-greeting p {
  margin: 0;
  color: #606266;
  font-size: 13px;
  line-height: 20px;
}

.creative-preset-bar {
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding: 8px 0;
  border-top: 1px solid #eef2f7;
  border-bottom: 1px solid #eef2f7;
}

.creative-preset-label,
.creative-preset-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.creative-preset-label {
  flex: none;
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}

.creative-preset-label i {
  color: #409eff;
  font-size: 16px;
}

.creative-preset-actions {
  min-width: 0;
  justify-content: flex-end;
}

.creative-preset-actions ::v-deep .el-select {
  width: 220px;
}

.creative-prompt-form-item {
  position: relative;
  margin-bottom: 12px;
}

::v-deep .creative-prompt-editor textarea {
  min-height: 180px !important;
  padding: 14px 16px;
  color: #111827;
  border-radius: 8px;
  font-size: 14px;
  line-height: 22px;
}

.creative-prompt-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 26px;
  margin-top: 2px;
}

.prompt-mention-trigger {
  color: #2563eb;
  font-weight: 600;
}

.prompt-mention-menu {
  position: absolute;
  z-index: 30;
  top: calc(100% - 2px);
  left: 0;
  width: min(420px, 100%);
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #d9dee8;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
}

.prompt-mention-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
}

.prompt-mention-head span {
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}

.prompt-mention-head small {
  color: #909399;
  font-size: 11px;
}

.prompt-mention-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 6px;
}

.prompt-mention-item {
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: 38px minmax(0, 1fr) 16px;
  gap: 9px;
  align-items: center;
  min-height: 50px;
  padding: 6px 8px;
  color: #303133;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
}

.prompt-mention-item:hover,
.prompt-mention-item.active {
  background: #eff6ff;
}

.prompt-mention-item > img,
.prompt-mention-item > video,
.prompt-mention-icon {
  display: flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: #eef2f7;
  border-radius: 6px;
  object-fit: cover;
}

.prompt-mention-icon.add {
  color: #2563eb;
  background: #eff6ff;
  border: 1px dashed #93c5fd;
}

.prompt-mention-copy {
  min-width: 0;
}

.prompt-mention-copy strong,
.prompt-mention-copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prompt-mention-copy strong {
  color: #303133;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}

.prompt-mention-copy small {
  color: #909399;
  font-size: 11px;
  line-height: 16px;
}

.prompt-mention-arrow {
  color: #c0c4cc;
  font-size: 12px;
}

.prompt-mention-file-input {
  display: none;
}

.creative-mode-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(92px, 1fr));
  gap: 8px;
  margin: 10px 0 16px;
}

.creative-mode-card {
  display: flex;
  min-width: 0;
  height: 78px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  color: #303133;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
}

.creative-mode-card i {
  color: #409eff;
  font-size: 17px;
}

.creative-mode-card span {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
}

.creative-mode-card small {
  display: block;
  width: 100%;
  overflow: hidden;
  color: #909399;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 15px;
}

.creative-mode-card.active {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #409eff;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.14);
}

.creative-mode-card.active small {
  color: #3b82f6;
}

.creative-settings {
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(220px, 1.5fr) repeat(4, minmax(104px, 0.78fr));
  gap: 10px;
  align-items: start;
}

.creative-settings ::v-deep .el-form-item {
  margin-bottom: 12px;
}

.creative-audio-control {
  display: flex;
  height: 28px;
  align-items: center;
  gap: 8px;
  color: #606266;
  white-space: nowrap;
  font-size: 12px;
}

.creative-advanced-toggle {
  display: flex;
  justify-content: flex-end;
  min-height: 24px;
  margin-top: -6px;
}

.creative-advanced-toggle i {
  margin-left: 5px;
}

.creative-advanced-settings {
  display: grid;
  grid-template-columns: minmax(180px, 240px);
  padding: 10px 12px 0;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.creative-advanced-settings ::v-deep .el-form-item {
  margin-bottom: 10px;
}

.creative-material-panel {
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid #eef2f7;
}

.creative-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.creative-section-head > div:first-child {
  display: flex;
  align-items: center;
  gap: 8px;
}

.creative-section-head strong {
  color: #111827;
  font-size: 14px;
  line-height: 20px;
}

.creative-section-head span {
  color: #909399;
  font-size: 12px;
}

.creative-section-head.compact {
  margin-bottom: 8px;
}

.creative-material-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.creative-media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.creative-media-card {
  min-width: 0;
  overflow: hidden;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.creative-media-card img,
.creative-media-card video,
.creative-audio-preview,
.creative-media-fallback {
  display: flex;
  width: 100%;
  height: 96px;
  align-items: center;
  justify-content: center;
  color: #909399;
  background: #eef2f7;
  object-fit: cover;
  font-size: 12px;
}

.creative-audio-preview {
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  box-sizing: border-box;
}

.creative-audio-preview > i {
  color: #64748b;
  font-size: 20px;
}

.creative-audio-preview audio {
  width: 100%;
  height: 32px;
}

.creative-media-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 0;
  color: #606266;
  font-size: 12px;
  line-height: 18px;
}

.creative-media-card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.creative-media-card-actions ::v-deep .el-button + .el-button {
  margin-left: 0;
}

.creative-media-name {
  padding: 0 8px 8px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 18px;
}

.creative-empty-material,
.creative-empty-recent {
  display: flex;
  min-height: 96px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #909399;
  background: #f8fafc;
  border: 1px dashed #d5dbe7;
  border-radius: 8px;
  font-size: 13px;
}

.creative-media-line {
  display: grid;
  grid-template-columns: 92px 128px minmax(180px, 1fr) 72px 32px;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.creative-side {
  position: sticky;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.creative-submit-card,
.creative-recent-card {
  padding: 14px;
}

.creative-submit-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.creative-submit-card strong,
.creative-submit-card span {
  display: block;
}

.creative-submit-card strong {
  color: #111827;
  font-size: 16px;
  line-height: 22px;
}

.creative-submit-card span {
  margin-top: 2px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 18px;
}

.creative-submit-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.creative-cost-estimate {
  min-height: 76px;
  padding: 10px 0;
  border-top: 1px solid #eef2f7;
  border-bottom: 1px solid #eef2f7;
}

.creative-cost-head,
.creative-cost-detail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.creative-cost-head > span {
  color: #606266;
  font-size: 12px;
  line-height: 20px;
}

.creative-cost-head strong {
  color: #111827;
  font-size: 20px;
  line-height: 26px;
}

.creative-cost-detail {
  margin-top: 4px;
}

.creative-cost-detail span,
.creative-cost-estimate small {
  color: #909399;
  font-size: 11px;
  line-height: 17px;
}

.creative-cost-estimate small {
  display: block;
  margin-top: 4px;
}

.creative-submit-meta span {
  margin: 0;
  padding: 6px 8px;
  color: #606266;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.creative-recent-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.creative-recent-item {
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  padding: 8px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.creative-recent-item video,
.creative-recent-item .creative-media-fallback {
  width: 84px;
  height: 56px;
  border-radius: 6px;
  object-fit: cover;
}

.creative-task-fallback i {
  color: #94a3b8;
  font-size: 20px;
}

.creative-recent-info {
  min-width: 0;
}

.creative-recent-title {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.creative-recent-title strong {
  min-width: 0;
}

.creative-recent-title ::v-deep .el-tag {
  flex: none;
}

.creative-recent-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.creative-recent-actions ::v-deep .el-button + .el-button {
  margin-left: 0;
}

.creative-recent-item strong,
.creative-recent-item span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.creative-recent-item strong {
  color: #303133;
  font-size: 13px;
  line-height: 18px;
}

.creative-recent-item span {
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.panel {
  margin-bottom: 14px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.panel-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 700;
}

.create-body {
  display: grid;
  grid-template-columns: minmax(760px, 1080px) minmax(320px, 1fr);
  gap: 22px;
  align-items: start;
}

.create-form {
  min-width: 0;
  max-width: none;
}

.prompt-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 24px;
  margin-top: 4px;
}

.continue-hint {
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.audio-generate-control {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 62px;
  white-space: nowrap;
}

.audio-generate-text {
  color: #606266;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}

.media-block {
  margin: 2px 0 14px 96px;
}

.sub-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
}

.media-row {
  display: grid;
  grid-template-columns: 92px 128px minmax(260px, 1fr) 76px 76px 34px;
  gap: 8px;
  margin-bottom: 8px;
  align-items: start;
}

.uploaded-name {
  grid-column: 3 / 4;
  margin-top: -6px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-preview-panel {
  min-width: 0;
  min-height: 204px;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
}

.media-preview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}

.media-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 8px;
  max-height: 254px;
  overflow: auto;
}

.media-preview-item {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #f8fafc;
}

.media-preview-delete {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  padding: 5px;
}

.media-preview-thumb {
  display: block;
  width: 100%;
  height: 96px;
  background: #000000;
  object-fit: contain;
}

.media-preview-audio {
  display: block;
  width: 100%;
  height: 40px;
  margin: 28px 0;
}

.media-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 96px;
  color: #909399;
  background: #f5f7fa;
  font-size: 12px;
}

.media-preview-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 0;
  color: #606266;
  font-size: 12px;
  line-height: 16px;
}

.media-preview-name {
  padding: 2px 8px 7px;
  color: #909399;
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 154px;
  color: #909399;
  background: #f8fafc;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
}

.picker-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

::v-deep .media-picker-dialog {
  max-width: calc(100vw - 32px);
}

.picker-media-preview {
  display: flex;
  width: 190px;
  min-height: 72px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.picker-media-preview video {
  display: block;
  width: 190px;
  height: 106px;
  background: #000000;
  object-fit: contain;
}

.picker-media-preview audio {
  width: 184px;
  height: 36px;
}

.picker-media-preview img {
  display: block;
  width: 100%;
  height: 106px;
  object-fit: contain;
}

.preview-video {
  width: 100%;
  max-height: 70vh;
  background: #000000;
  border-radius: 4px;
  display: block;
}

.preview-actions {
  margin-top: 10px;
  text-align: right;
}

.result-video-cell {
  width: 210px;
}

.result-video {
  width: 210px;
  height: 118px;
  display: block;
  background: #000000;
  border-radius: 4px;
  object-fit: contain;
}

.task-model-cell {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  line-height: 1.35;
}

.task-model-name {
  min-width: 0;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-model-id {
  min-width: 0;
  color: #909399;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.billing-panel {
  padding-bottom: 12px;
}

.billing-title-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
}

.billing-summary-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.billing-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.billing-card span {
  color: #606266;
  font-size: 12px;
  line-height: 18px;
}

.billing-card strong {
  color: #111827;
  font-size: 20px;
  line-height: 26px;
  font-weight: 700;
}

.billing-table {
  margin-top: 8px;
}

.billing-sub-title {
  margin-top: 12px;
  color: #303133;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
}

.billing-usage {
  color: #303133;
  line-height: 18px;
}

.billing-cost {
  color: #16a34a;
  font-weight: 700;
  line-height: 18px;
}

.provider-key-option {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.provider-key-option span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.provider-key-option small {
  flex: 0 0 auto;
  color: #909399;
  font-size: 12px;
}

.query-form {
  margin-bottom: -12px;
}

.danger-link {
  color: #f56c6c;
}

.share-hint {
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.favorite-active {
  color: #e6a23c;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  color: #606266;
  font-size: 13px;
}

::v-deep .event-payload-pre {
  max-height: 520px;
  overflow: auto;
  margin: 0;
  padding: 12px;
  color: #303133;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 980px) {
  .page-head,
  .panel-title,
  .pagination-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .creative-workspace {
    grid-template-columns: 1fr;
  }

  .creative-side {
    position: static;
  }

  .creative-main {
    padding: 16px;
  }

  .creative-greeting h3 {
    font-size: 22px;
    line-height: 30px;
  }

  .creative-mode-grid,
  .creative-settings {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .creative-media-line {
    grid-template-columns: 1fr;
  }

  .creative-material-actions {
    justify-content: flex-start;
  }

  .creative-preset-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .creative-preset-actions {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .create-body {
    grid-template-columns: 1fr;
  }

  .media-block {
    margin-left: 0;
  }

  .media-row {
    grid-template-columns: 1fr;
  }

  .billing-summary-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 620px) {
  .aigc-video-page {
    padding: 12px;
  }

  .creative-mode-grid,
  .creative-settings,
  .creative-advanced-settings {
    grid-template-columns: 1fr;
  }

  .creative-material-actions {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .creative-material-actions > *,
  .creative-material-actions ::v-deep .el-button {
    width: 100%;
  }
}
</style>
